import React, { useState, useEffect } from 'react';
import { supabase, insertRow, updateRow, deleteRow } from '../../db/engine';
import { Plus, X, Edit2, Trash2, Shield, Users, Building, Tag, List, Link, Layers, FileText, Eye, EyeOff, Bell, Mail, KeyRound } from 'lucide-react';
import CoberturaDesc from '../../components/CoberturaDesc';
import { getConfig, updateConfig } from '../../db/contabilidad';
import { setMoneda, MONEDAS } from '../../utils/moneda';
import { sincronizarAsignaciones } from '../../db/crmAsignacion';
import { useApp } from '../../context/AppContext';
import { abrirDocumento } from '../../utils/archivos';
import { MARCA_DEFAULT, FUENTES, aplicarColoresCSS, aplicarFuente, aplicarMarca, getMarca, procesarLogo, revisarLegibilidad } from '../../utils/marca';
import { problemaClave } from '../../utils/clave';
import { LogoMarca, NombreMarca } from '../../components/Marca';
import { Palette, DollarSign } from 'lucide-react';
import MiPlan from './MiPlan';

// Duraciones que el cliente puede elegir para la ventana de acceso de soporte.
// Deben coincidir con DURACIONES_MIN en functions/api/soporte.js.
const DURACIONES_SOPORTE = [
  { min: 30,   label: '30 minutos' },
  { min: 60,   label: '1 hora' },
  { min: 240,  label: '4 horas' },
  { min: 480,  label: '8 horas' },
  { min: 1440, label: '24 horas' },
];

const MODULOS = [
  { key:'dashboard',   label:'Dashboard' },
  { key:'clientes',    label:'Clientes' },
  { key:'polizas',     label:'Pólizas' },
  { key:'pagos',       label:'Pagos' },
  { key:'comisiones',  label:'Comisiones' },
  { key:'contabilidad',label:'Contabilidad' },
  { key:'crm',         label:'CRM Gestión' },
  { key:'reclamos',    label:'Reclamos' },
  { key:'agentes',     label:'Agentes' },
  { key:'archivos',    label:'Archivos' },
  { key:'solicitudes', label:'Solicitudes' },
  { key:'cobros',      label:'Cobros' },
  { key:'reportes',    label:'Reportes' },
  { key:'admin',       label:'Administración' },
];

const defaultPermisos = () => {
  const p = {};
  MODULOS.forEach(m => { p[m.key] = { ver:false, editar:false }; });
  return p;
};

// Targets de endoso = QUÉ CAMPO toca el cambio al facturar. El motor (src/db/endosos.js)
// sabe aplicar y reversar cada uno. "libre" = solo descriptivo (no toca campos; la prima
// la mueve el flujo igual). Los de vehículo solo aplican al ramo Auto.
const ENDOSO_TARGETS = [
  { key:'libre',              label:'Sin cambio estructural (solo descriptivo)' },
  { key:'suma_cambio',        label:'Cambio de suma asegurada' },
  { key:'deducible_cambio',   label:'Cambio de deducible' },
  { key:'cobertura_inclusion',label:'Inclusión de cobertura' },
  { key:'cobertura_exclusion',label:'Exclusión de cobertura' },
  { key:'datos_generales',    label:'Cambio de datos (asegurado / beneficiario / riesgo)' },
  { key:'forma_pago',         label:'Cambio de forma / frecuencia de pago' },
  { key:'fecha_pago',         label:'Cambio de día de pago (re-fecha cuotas pendientes)' },
  { key:'vigencia',           label:'Cambio de vigencia (fechas)' },
  { key:'vehiculo_inclusion', label:'Auto — Inclusión de unidad' },
  { key:'vehiculo_exclusion', label:'Auto — Exclusión de unidad' },
  { key:'vehiculo_cambio',    label:'Auto — Cambio de vehículo' },
  { key:'vehiculo_datos',     label:'Auto — Cambio de datos de la unidad' },
];
const targetLabel = (t) => ENDOSO_TARGETS.find(x => x.key === t)?.label || (t || '—');

// Tipos de notificación por correo. Las variables {{...}} se reemplazan con datos
// reales al enviar (en el CRM para cobro/renovación; en el envío diario de cumpleaños).
const NOTIF_TIPOS = [
  { key:'cobro',      label:'Cobro / Saldo pendiente', desc:'Se envía a un clic desde la tarea de COBRO en el CRM.',
    vars:['cliente','numero_poliza','aseguradora','saldo','fecha_vencimiento','correo'] },
  { key:'renovacion', label:'Renovación por vencer',   desc:'Se envía a un clic desde la tarea de RENOVACIÓN en el CRM.',
    vars:['cliente','numero_poliza','aseguradora','fecha_vencimiento','fecha_inicio','correo'] },
  { key:'cumpleanos', label:'Cumpleaños',              desc:'Envío AUTOMÁTICO diario a los clientes que cumplen años.',
    vars:['cliente','primer_nombre','correo'] },
];

const colBtn = {
  fontSize:10, padding:'2px 6px', borderRadius:5, cursor:'pointer',
  border:'1px solid rgba(var(--silver-rgb),0.3)', background:'transparent',
  color:'var(--silver)', fontFamily:'var(--font)',
};

export default function Admin() {
  const { user, refrescarMarca } = useApp();
  const [tab, setTab]                   = useState('roles');
  const [roles, setRoles]               = useState([]);
  const [usuarios, setUsuarios]         = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [ramos, setRamos]               = useState([]);
  const [ejecutivos, setEjecutivos]     = useState([]);
  const [agentesList, setAgentesList]   = useState([]);
  const [modal, setModal]               = useState(null);
  const [form, setForm]                 = useState({});
  const [selected, setSelected]         = useState(null);
  const [permisos, setPermisos]         = useState(defaultPermisos());
  const [crmCfg, setCrmCfg]             = useState({}); // cont_config (responsables de cobros/reclamos)
  const [savingCfg, setSavingCfg]       = useState(false);
  const [showPwd, setShowPwd]           = useState(false);
  const [cedulaFile, setCedulaFile]     = useState(null);
  const [savingUsuario, setSavingUsuario] = useState(false);
  const [savingSoporte, setSavingSoporte] = useState(false);
  const [soporteMin, setSoporteMin]       = useState(60); // duración elegida para la ventana
  const [usuarioReadOnly, setUsuarioReadOnly] = useState(false); // modal de usuario en modo "Ver"

  // ── Marca del sistema (white-label) ──
  const [marcaForm, setMarcaForm] = useState(null); // se inicializa al abrir la pestaña
  const [savingMarca, setSavingMarca] = useState(false);
  const mf = (k, v) => setMarcaForm(p => ({ ...p, [k]: v }));

  // Notificaciones por correo (plantillas + config del remitente)
  const [notifPlantillas, setNotifPlantillas] = useState({}); // { tipo: {tipo, activo, asunto, cuerpo} }
  const [notifConfig, setNotifConfig]         = useState({ remitente_nombre:'', remitente_correo:'', proveedor:'', habilitado:false });
  const [savingNotif, setSavingNotif]         = useState(''); // tipo o 'config' que se está guardando
  const [enviandoCumple, setEnviandoCumple]   = useState(false);

  useEffect(() => { getConfig().then(c => setCrmCfg(c || {})); }, []);

  const guardarResponsables = async () => {
    setSavingCfg(true);
    try {
      await updateConfig({
        responsable_cobros_usuario_id:   crmCfg.responsable_cobros_usuario_id   || null,
        responsable_reclamos_usuario_id: crmCfg.responsable_reclamos_usuario_id || null,
      });
      // Reasignar en el acto las tareas abiertas al nuevo responsable
      let n = 0; try { n = await sincronizarAsignaciones(); } catch {}
      alert(`Responsables guardados.${n ? ` Se reasignaron ${n} tarea(s) abierta(s).` : ''}`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingCfg(false); }
  };

  const guardarMoneda = async () => {
    setSavingCfg(true);
    try {
      const m = crmCfg.moneda || 'B/.';
      await updateConfig({ moneda: m });
      setMoneda(m);
      alert('Moneda guardada. Recarga la página (Ctrl+Shift+R) para aplicarla en todas las pantallas.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingCfg(false); }
  };

  // Inicializa el formulario de marca desde la config al abrir la pestaña.
  useEffect(() => {
    if (tab !== 'marca') return;
    const c = crmCfg || {};
    const d = MARCA_DEFAULT;
    setMarcaForm({
      marca_nombre:       c.marca_nombre       || d.nombre,
      marca_nombre_corto: c.marca_nombre_corto ?? d.nombre_corto,
      marca_tagline:      c.marca_tagline      || d.tagline,
      marca_abreviatura:  c.marca_abreviatura  || d.abreviatura,
      marca_fuente:       c.marca_fuente       || d.fuente,
      marca_logo_url:     c.marca_logo_url     || '',
      marca_logo_escala:  Number(c.marca_logo_escala) || 1,
      color_navy:         c.color_navy      || d.colores.navy,
      color_steel:        c.color_steel     || d.colores.steel,
      color_sky:          c.color_sky       || d.colores.sky,
      color_sky_light:    c.color_sky_light || d.colores.sky_light,
      color_silver:       c.color_silver    || d.colores.silver,
    });
  }, [tab, crmCfg]);

  // Vista previa en vivo: aplica los colores del formulario mientras se editan.
  // Al salir de la pestaña, revierte a la marca guardada.
  useEffect(() => {
    if (tab !== 'marca' || !marcaForm) return;
    aplicarColoresCSS({
      navy: marcaForm.color_navy, steel: marcaForm.color_steel, sky: marcaForm.color_sky,
      sky_light: marcaForm.color_sky_light, silver: marcaForm.color_silver,
    });
    aplicarFuente(marcaForm.marca_fuente);
    return () => { aplicarColoresCSS(getMarca().colores); aplicarFuente(getMarca().fuente); };
  }, [tab, marcaForm]);

  const [logoErr, setLogoErr] = useState('');
  const subirLogo = async (file) => {
    setLogoErr('');
    try {
      const uri = await procesarLogo(file);
      mf('marca_logo_url', uri);
    } catch (e) { setLogoErr(e.message || 'No se pudo procesar el logo.'); }
  };

  const restaurarMarcaDefault = () => {
    const d = MARCA_DEFAULT;
    setMarcaForm(p => ({
      ...p,
      color_navy: d.colores.navy, color_steel: d.colores.steel, color_sky: d.colores.sky,
      color_sky_light: d.colores.sky_light, color_silver: d.colores.silver,
    }));
  };

  // Legibilidad de la paleta en edición. null = algún color aún no es un #RRGGBB completo.
  const problemasLegibilidad = () => {
    if (!marcaForm) return null;
    const cs = [marcaForm.color_navy, marcaForm.color_steel, marcaForm.color_sky, marcaForm.color_sky_light, marcaForm.color_silver];
    if (!cs.every(x => /^#[0-9a-f]{6}$/i.test(String(x || '')))) return null;
    return revisarLegibilidad({
      navy: marcaForm.color_navy, steel: marcaForm.color_steel, sky: marcaForm.color_sky,
      sky_light: marcaForm.color_sky_light, silver: marcaForm.color_silver,
    });
  };

  const guardarMarca = async () => {
    if (!marcaForm) return;
    // Freno suave: si algún texto quedaría ilegible, se avisa y se pide confirmar.
    const problemas = problemasLegibilidad();
    if (problemas?.length && !window.confirm(
      'Con esta combinación de colores algunos textos podrían no verse bien:\n\n'
      + problemas.map(p => `• ${p.que} (ajusta ${p.cambia})`).join('\n')
      + '\n\n¿Guardar de todas formas?')) return;
    setSavingMarca(true);
    try {
      const payload = {
        marca_nombre:       marcaForm.marca_nombre?.trim()       || null,
        marca_nombre_corto: marcaForm.marca_nombre_corto?.trim() || null,
        marca_tagline:      marcaForm.marca_tagline?.trim()      || null,
        marca_abreviatura:  marcaForm.marca_abreviatura?.trim()  || null,
        marca_fuente:       marcaForm.marca_fuente               || null,
        marca_logo_url:     marcaForm.marca_logo_url?.trim()     || null,
        marca_logo_escala:  Number(marcaForm.marca_logo_escala) || 1,
        color_navy:         marcaForm.color_navy,
        color_steel:        marcaForm.color_steel,
        color_sky:          marcaForm.color_sky,
        color_sky_light:    marcaForm.color_sky_light,
        color_silver:       marcaForm.color_silver,
      };
      const saved = await updateConfig(payload);
      setCrmCfg(c => ({ ...c, ...saved }));
      aplicarMarca(saved);          // aplica de una en esta pantalla
      refrescarMarca?.();           // propaga a sidebar/header/toda la app
      alert('Marca guardada y aplicada.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingMarca(false); }
  };

  // Coberturas
  const [ramoActivo, setRamoActivo]         = useState(null);
  const [coberturas, setCoberturas]         = useState([]);
  const [cobForm, setCobForm]               = useState({ nombre:'', descripcion:'', orden:0, es_basica:true });
  const [cobSelected, setCobSelected]       = useState(null);

  // Coberturas por subramo
  const [cobSubramoActivo, setCobSubramoActivo] = useState(null);
  const [cobSubramo, setCobSubramo]             = useState([]);
  const [cobSubForm, setCobSubForm]             = useState({ nombre:'', descripcion:'', orden:0, es_basica:true });

  // Vinculación aseguradora-ramo
  const [asegRamos, setAsegRamos]           = useState([]);
  const [asegRamoForm, setAsegRamoForm]     = useState({ aseguradora_id:'', comision:'' });
  const [loadingAsegRamos, setLoadingAsegRamos] = useState(false);

  // Grupos económicos
  const [grupos, setGrupos]                 = useState([]);
  const [grupoForm, setGrupoForm]           = useState({ nombre:'', descripcion:'' });
  const [grupoSelected, setGrupoSelected]   = useState(null);

  // Subramos
  const [subRamoActivo, setSubRamoActivo]   = useState(null);
  const [subramos, setSubramos]             = useState([]);
  const [subForm, setSubForm]               = useState({ nombre:'', codigo:'' });
  const [subSelected, setSubSelected]       = useState(null);

  // Conteo de pólizas por ramo/subramo
  const [ramoPolizas, setRamoPolizas]       = useState({});
  const [asegPolizas, setAsegPolizas]       = useState({});
  const [subPolizas, setSubPolizas]         = useState({});

  // Modal de reemplazo antes de eliminar
  const [replaceModal, setReplaceModal]     = useState(null);
  // { type:'ramo'|'subramo', item, count, replacementId:'', options:[] }

  // Subramo-Aseguradoras (muchos-a-muchos)
  const [subAsegActivo, setSubAsegActivo]   = useState(null);
  const [subAsegList, setSubAsegList]       = useState([]);
  const [subAsegForm, setSubAsegForm]       = useState({ aseguradora_id:'', porcentaje_comision:'', base_comision:'sin_impuesto' });

  // Tipos de Documento
  const [tiposDoc, setTiposDoc]             = useState([]);
  const [tipoDocForm, setTipoDocForm]       = useState({ nombre:'', codigo:'', categoria:'otro', requiere_anio:false });
  const [tipoDocSelected, setTipoDocSelected] = useState(null);
  const [savingTipoDoc, setSavingTipoDoc]   = useState(false);

  // Catálogo de documentos de Endoso/Póliza (catálogo plano)
  const [catEndoso, setCatEndoso]           = useState([]);
  const [catEndosoForm, setCatEndosoForm]   = useState({ nombre:'' });
  const [catEndosoSelected, setCatEndosoSelected] = useState(null);
  const [savingCatEndoso, setSavingCatEndoso] = useState(false);

  // ── Catálogo de TIPOS DE ENDOSO (con comportamiento que dispara la lógica) ──
  const [tipoEndoso, setTipoEndoso]               = useState([]);
  const [tipoEndosoForm, setTipoEndosoForm]       = useState({ nombre:'', comportamiento:'normal', target:'libre', ramo_id:'' });
  const [tipoEndosoSelected, setTipoEndosoSelected] = useState(null);
  const [savingTipoEndoso, setSavingTipoEndoso]   = useState(false);

  // ── Referidos (referidores) ──────────────────────────────────
  const [referidos, setReferidos]               = useState([]);
  const [referidoForm, setReferidoForm]         = useState({ nombre:'', telefono:'', email:'', comision_nueva_pct:'', comision_renovacion_pct:'', notas:'' });
  const [referidoSelected, setReferidoSelected] = useState(null);
  const [savingReferido, setSavingReferido]     = useState(false);

  const load = async () => {
    const [r, u, a, ra, ej] = await Promise.all([
      supabase.from('roles').select('*').order('es_master', { ascending:false }).order('nombre'),
      supabase.from('usuarios').select('*, roles(nombre)').order('nombre'),
      supabase.from('aseguradoras').select('*').order('nombre'),
      supabase.from('ramos').select('*').order('nombre'),
      supabase.from('ejecutivos').select('*').order('nombre'),
    ]);
    setRoles(r.data || []);
    setUsuarios((u.data || []).map(x => ({ ...x, rol_nombre: x.roles?.nombre })));
    setAseguradoras(a.data || []);
    setRamos(ra.data || []);
    setEjecutivos(ej.data || []);
    const { data: agData } = await supabase.from('agentes').select('id, nombre').eq('activo', true).order('nombre');
    setAgentesList(agData || []);
    const { data: gdata } = await supabase.from('grupos_economicos').select('*').order('nombre');
    setGrupos(gdata || []);

    // Conteo de pólizas por ramo y por aseguradora
    const { data: pcRamo } = await supabase.from('polizas').select('ramo_id, aseguradora_id');
    const ramoMap = {}; const asegMap = {};
    (pcRamo || []).forEach(p => {
      if (p.ramo_id) ramoMap[p.ramo_id] = (ramoMap[p.ramo_id] || 0) + 1;
      if (p.aseguradora_id) asegMap[p.aseguradora_id] = (asegMap[p.aseguradora_id] || 0) + 1;
    });
    setRamoPolizas(ramoMap);
    setAsegPolizas(asegMap);
  };

  const saveGrupo = async () => {
    if (!grupoForm.nombre?.trim()) return;
    try {
      const data = { nombre: grupoForm.nombre.trim(), descripcion: grupoForm.descripcion||null, activo: true };
      if (grupoSelected) await updateRow('grupos_economicos', grupoSelected.id, data);
      else await insertRow('grupos_economicos', data);
      setGrupoForm({ nombre:'', descripcion:'' }); setGrupoSelected(null);
      const { data: gdata } = await supabase.from('grupos_economicos').select('*').order('nombre');
      setGrupos(gdata || []);
    } catch (e) { alert('Error: ' + e.message); }
  };

  const deleteGrupo = async (id) => {
    if (!window.confirm('¿Eliminar este grupo económico?')) return;
    try { await deleteRow('grupos_economicos', id); setGrupos(g => g.filter(x => x.id !== id)); }
    catch (e) { alert('Error: ' + e.message); }
  };

  const saveEjecutivo = async () => {
    if (!form.nombre) return;
    try {
      const data = { nombre:form.nombre, email:form.email||null, telefono:form.telefono||null, activo:form.activo!==false,
        ejec_cuenta:!!form.ejec_cuenta, ejec_cobro:!!form.ejec_cobro, ejec_reclamo:!!form.ejec_reclamo };
      if (selected) await updateRow('ejecutivos', selected.id, data);
      else await insertRow('ejecutivos', data);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message||'')); }
  };

  const toggleEjecutivo = async (e) => {
    await updateRow('ejecutivos', e.id, { activo: !e.activo });
    await load();
  };
  useEffect(() => { load(); }, []);

  const loadTiposDoc = async () => {
    const { data } = await supabase.from('tipos_documento').select('*').order('categoria').order('orden');
    setTiposDoc(data || []);
  };

  useEffect(() => {
    if (tab === 'documentos') loadTiposDoc();
    if (tab === 'notificaciones') loadNotificaciones();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ── Notificaciones por correo ─────────────────────────────────
  const loadNotificaciones = async () => {
    const [{ data: pl }, { data: cfg }] = await Promise.all([
      supabase.from('notificacion_plantillas').select('*').order('tipo'),
      supabase.from('notificacion_config').select('*').eq('id', 1).maybeSingle(),
    ]);
    const map = {};
    (pl || []).forEach(p => { map[p.tipo] = p; });
    setNotifPlantillas(map);
    if (cfg) setNotifConfig({
      remitente_nombre: cfg.remitente_nombre || '',
      remitente_correo: cfg.remitente_correo || '',
      proveedor:        cfg.proveedor || '',
      habilitado:       !!cfg.habilitado,
    });
  };

  // Interruptor maestro global (solo el usuario master lo prende/apaga).
  // Apagado = la función está montada pero el envío real no está activo:
  // en el CRM los botones de "Enviar correo" salen deshabilitados para todos.
  const toggleNotifHabilitado = async () => {
    if (!user?.es_master) return;
    const nuevo = !notifConfig.habilitado;
    if (nuevo && !window.confirm('Vas a ACTIVAR el envío de correos. Hazlo solo cuando la API key del proveedor ya esté puesta en Cloudflare. ¿Continuar?')) return;
    setNotifConfig(p => ({ ...p, habilitado: nuevo }));
    try {
      await supabase.from('notificacion_config')
        .update({ habilitado: nuevo, actualizado_en: new Date().toISOString() })
        .eq('id', 1);
    } catch (e) { alert('Error: ' + (e.message || '')); setNotifConfig(p => ({ ...p, habilitado: !nuevo })); }
  };

  const setPlantillaField = (tipo, field, val) =>
    setNotifPlantillas(p => ({ ...p, [tipo]: { ...(p[tipo] || { tipo }), [field]: val } }));

  const saveNotifPlantilla = async (tipo) => {
    const pl = notifPlantillas[tipo] || { tipo };
    setSavingNotif(tipo);
    try {
      await supabase.from('notificacion_plantillas').upsert({
        tipo,
        activo:  !!pl.activo,
        asunto:  pl.asunto || '',
        cuerpo:  pl.cuerpo || '',
        actualizado_en: new Date().toISOString(),
      }, { onConflict: 'tipo' });
      alert('Plantilla guardada.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingNotif(''); }
  };

  const toggleNotifPlantilla = async (tipo) => {
    const nuevo = !(notifPlantillas[tipo]?.activo);
    setPlantillaField(tipo, 'activo', nuevo);
    try {
      await supabase.from('notificacion_plantillas')
        .update({ activo: nuevo, actualizado_en: new Date().toISOString() })
        .eq('tipo', tipo);
    } catch (e) { alert('Error: ' + (e.message || '')); setPlantillaField(tipo, 'activo', !nuevo); }
  };

  // Prueba manual del envío de cumpleaños de hoy (el automático lo dispara el cron diario).
  const enviarCumpleanosHoy = async () => {
    setEnviandoCumple(true);
    try {
      const r = await fetch('/api/cron-cumpleanos', { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (data.ok && data.sent != null && data.reason == null) {
        alert(`Cumpleaños de hoy: ${data.candidatos || 0} cumpleañero(s), ${data.sent} enviado(s), ${data.fail || 0} con error, ${data.omitidos || 0} ya enviados.`);
      } else {
        const motivos = {
          notificaciones_desactivadas: 'Primero activa el interruptor maestro arriba.',
          plantilla_inactiva: 'Activa la plantilla de cumpleaños.',
          correo_no_configurado: 'Falta poner la API key del correo en Cloudflare (envío aún dormido).',
          sin_supabase: 'Falta la configuración de Supabase en la función.',
        };
        alert('No se envió. ' + (motivos[data.reason] || data.reason || 'Motivo desconocido.'));
      }
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setEnviandoCumple(false); }
  };

  const saveNotifConfig = async () => {
    setSavingNotif('config');
    try {
      await supabase.from('notificacion_config').update({
        remitente_nombre: notifConfig.remitente_nombre || null,
        remitente_correo: notifConfig.remitente_correo || null,
        proveedor:        notifConfig.proveedor || null,
        actualizado_en:   new Date().toISOString(),
      }).eq('id', 1);
      alert('Configuración del remitente guardada.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingNotif(''); }
  };

  const saveTipoDoc = async () => {
    if (!tipoDocForm.nombre?.trim()) { alert('El nombre es requerido.'); return; }
    setSavingTipoDoc(true);
    try {
      const data = {
        nombre:       tipoDocForm.nombre.trim(),
        codigo:       tipoDocForm.codigo?.trim() || null,
        categoria:    tipoDocForm.categoria || 'otro',
        requiere_anio: tipoDocForm.requiere_anio || false,
        activo:       true,
      };
      if (tipoDocSelected) {
        await updateRow('tipos_documento', tipoDocSelected.id, data);
      } else {
        // Calcular orden: max + 1 por categoría
        const same = tiposDoc.filter(t => t.categoria === data.categoria);
        data.orden = same.length > 0 ? Math.max(...same.map(t => t.orden || 0)) + 1 : 1;
        await insertRow('tipos_documento', data);
      }
      setTipoDocForm({ nombre:'', codigo:'', categoria:'otro', requiere_anio:false });
      setTipoDocSelected(null);
      await loadTiposDoc();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingTipoDoc(false); }
  };

  const toggleTipoDoc = async (t) => {
    try {
      await updateRow('tipos_documento', t.id, { activo: !t.activo });
      await loadTiposDoc();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // ── Catálogo de documentos Endoso/Póliza ──────────────────────
  const loadCatEndoso = async () => {
    const { data } = await supabase.from('catalogo_documentos').select('*').order('orden').order('nombre');
    setCatEndoso(data || []);
  };

  useEffect(() => {
    if (tab === 'catalogo') loadCatEndoso();
    if (tab === 'tipos_endoso') loadTipoEndoso();
    if (tab === 'referidos') loadReferidos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ── Referidos ─────────────────────────────────────────────────
  const loadReferidos = async () => {
    const { data } = await supabase.from('referidos').select('*').order('nombre');
    setReferidos(data || []);
  };

  const saveReferido = async () => {
    if (!referidoForm.nombre?.trim()) { alert('El nombre es requerido.'); return; }
    setSavingReferido(true);
    try {
      const numPct = (v) => (v === '' || v == null ? null : parseFloat(v));
      const nuevaPct = numPct(referidoForm.comision_nueva_pct);
      const data = {
        nombre:       referidoForm.nombre.trim(),
        telefono:     referidoForm.telefono?.trim() || null,
        email:        referidoForm.email?.trim() || null,
        comision_nueva_pct:      nuevaPct,
        comision_renovacion_pct: numPct(referidoForm.comision_renovacion_pct),
        comision_pct: nuevaPct, // legado: se mantiene = % de nueva
        notas:        referidoForm.notas?.trim() || null,
      };
      if (referidoSelected) {
        await updateRow('referidos', referidoSelected.id, data);
      } else {
        data.activo = true;
        await insertRow('referidos', data);
      }
      setReferidoForm({ nombre:'', telefono:'', email:'', comision_nueva_pct:'', comision_renovacion_pct:'', notas:'' });
      setReferidoSelected(null);
      await loadReferidos();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingReferido(false); }
  };

  const toggleReferido = async (r) => {
    try {
      await updateRow('referidos', r.id, { activo: !r.activo });
      await loadReferidos();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // ── Catálogo de TIPOS DE ENDOSO ───────────────────────────────
  const loadTipoEndoso = async () => {
    const { data } = await supabase.from('catalogo_endoso_tipos').select('*').order('orden').order('nombre');
    setTipoEndoso(data || []);
  };

  const saveTipoEndoso = async () => {
    if (!tipoEndosoForm.nombre?.trim()) { alert('El nombre es requerido.'); return; }
    setSavingTipoEndoso(true);
    try {
      const data = {
        nombre: tipoEndosoForm.nombre.trim(),
        comportamiento: tipoEndosoForm.comportamiento || 'normal',
        target: tipoEndosoForm.target || 'libre',
        ramo_id: tipoEndosoForm.ramo_id ? parseInt(tipoEndosoForm.ramo_id) : null,
      };
      if (tipoEndosoSelected) {
        await updateRow('catalogo_endoso_tipos', tipoEndosoSelected.id, data);
      } else {
        data.activo = true;
        data.orden = tipoEndoso.length > 0 ? Math.max(...tipoEndoso.map(c => c.orden || 0)) + 1 : 1;
        await insertRow('catalogo_endoso_tipos', data);
      }
      setTipoEndosoForm({ nombre:'', comportamiento:'normal', target:'libre', ramo_id:'' });
      setTipoEndosoSelected(null);
      await loadTipoEndoso();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingTipoEndoso(false); }
  };

  const toggleTipoEndoso = async (c) => {
    try {
      await updateRow('catalogo_endoso_tipos', c.id, { activo: !c.activo });
      await loadTipoEndoso();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const saveCatEndoso = async () => {
    if (!catEndosoForm.nombre?.trim()) { alert('El nombre es requerido.'); return; }
    setSavingCatEndoso(true);
    try {
      const data = { nombre: catEndosoForm.nombre.trim(), activo: true };
      if (catEndosoSelected) {
        await updateRow('catalogo_documentos', catEndosoSelected.id, data);
      } else {
        data.orden = catEndoso.length > 0 ? Math.max(...catEndoso.map(c => c.orden || 0)) + 1 : 1;
        await insertRow('catalogo_documentos', data);
      }
      setCatEndosoForm({ nombre:'' });
      setCatEndosoSelected(null);
      await loadCatEndoso();
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingCatEndoso(false); }
  };

  const toggleCatEndoso = async (c) => {
    try {
      await updateRow('catalogo_documentos', c.id, { activo: !c.activo });
      await loadCatEndoso();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const loadCoberturas = async (ramoId) => {
    const { data } = await supabase.from('coberturas')
      .select('*').eq('ramo_id', ramoId).is('subramo_id', null).order('orden').order('nombre');
    setCoberturas(data || []);
  };

  const loadCobSubramo = async (subramoId) => {
    const { data } = await supabase.from('coberturas')
      .select('*').eq('subramo_id', subramoId).order('es_basica', { ascending:false }).order('orden').order('nombre');
    setCobSubramo(data || []);
  };

  const openCoberturas = async (ramo) => {
    setRamoActivo(ramo);
    setCobForm({ nombre:'', descripcion:'', orden:0, es_basica:true });
    setCobSelected(null);
    await loadCoberturas(ramo.id);
    setModal('coberturas');
  };

  const openCobSubramo = async (subramo) => {
    setCobSubramoActivo(subramo);
    setCobSubForm({ nombre:'', descripcion:'', orden:0, es_basica:true });
    await loadCobSubramo(subramo.id);
    setModal('cob_subramo');
  };

  const saveCobSubramo = async () => {
    if (!cobSubForm.nombre.trim()) return;
    try {
      const payload = {
        ramo_id: subRamoActivo?.id || cobSubramoActivo.ramo_id,
        subramo_id: cobSubramoActivo.id,
        nombre: cobSubForm.nombre.trim(),
        descripcion: cobSubForm.descripcion || null,
        orden: parseInt(cobSubForm.orden) || 0,
        es_basica: cobSubForm.es_basica,
        activa: true,
      };
      await insertRow('coberturas', payload);
      await loadCobSubramo(cobSubramoActivo.id);
      setCobSubForm({ nombre:'', descripcion:'', orden:0, es_basica:true });
    } catch (e) { alert('Error: ' + (e.message||'')); }
  };

  const deleteCobSubramo = async (id) => {
    if (!window.confirm('¿Eliminar esta cobertura?')) return;
    await deleteRow('coberturas', id);
    await loadCobSubramo(cobSubramoActivo.id);
  };

  const toggleCobSubramoBasica = async (c) => {
    await updateRow('coberturas', c.id, { es_basica: !c.es_basica });
    await loadCobSubramo(cobSubramoActivo.id);
  };

  const loadSubramos = async (ramoId) => {
    const { data } = await supabase.from('subramos').select('*, subramo_aseguradoras(id)').eq('ramo_id', ramoId).order('nombre');
    setSubramos(data || []);
    const { data: pcSub } = await supabase.from('polizas').select('subramo_id').eq('ramo_id', ramoId);
    const subMap = {};
    (pcSub || []).forEach(p => { if (p.subramo_id) subMap[p.subramo_id] = (subMap[p.subramo_id] || 0) + 1; });
    setSubPolizas(subMap);
  };

  const openSubramos = async (ramo) => {
    setSubRamoActivo(ramo);
    setSubForm({ nombre:'', codigo:'' });
    setSubSelected(null);
    await loadSubramos(ramo.id);
    setModal('subramos');
  };

  const saveSubramo = async () => {
    if (!subForm.nombre.trim()) return;
    try {
      const payload = {
        ramo_id: subRamoActivo.id,
        nombre: subForm.nombre.trim(),
        codigo: subForm.codigo || null,
        activo: true,
      };
      if (subSelected) await updateRow('subramos', subSelected.id, payload);
      else await insertRow('subramos', payload);
      await loadSubramos(subRamoActivo.id);
      setSubForm({ nombre:'', codigo:'' }); setSubSelected(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const loadSubAseg = async (subramoId) => {
    const { data } = await supabase.from('subramo_aseguradoras')
      .select('*, aseguradoras(nombre)').eq('subramo_id', subramoId).order('id');
    setSubAsegList(data || []);
  };

  const openSubAseg = async (subramo) => {
    setSubAsegActivo(subramo);
    setSubAsegForm({ aseguradora_id:'', porcentaje_comision:'', base_comision:'sin_impuesto' });
    await loadSubAseg(subramo.id);
    setModal('subramo_aseg');
  };

  const saveSubAseg = async () => {
    if (!subAsegForm.aseguradora_id) return;
    try {
      await insertRow('subramo_aseguradoras', {
        subramo_id: subAsegActivo.id,
        aseguradora_id: parseInt(subAsegForm.aseguradora_id),
        porcentaje_comision: subAsegForm.porcentaje_comision ? parseFloat(subAsegForm.porcentaje_comision) : null,
        base_comision: subAsegForm.base_comision || 'sin_impuesto',
      });
      setSubAsegForm({ aseguradora_id:'', porcentaje_comision:'', base_comision:'sin_impuesto' });
      await loadSubAseg(subAsegActivo.id);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const deleteSubAseg = async (id) => {
    if (!window.confirm('¿Eliminar esta vinculación?')) return;
    await deleteRow('subramo_aseguradoras', id);
    await loadSubAseg(subAsegActivo.id);
  };

  const deleteSubramo = async (s) => {
    const count = subPolizas[s.id] || 0;
    if (count > 0) {
      const opts = subramos.filter(x => x.id !== s.id);
      setReplaceModal({ type:'subramo', item:s, count, replacementId:'', options:opts });
      return;
    }
    if (!window.confirm(`¿Eliminar subramo "${s.nombre}"?`)) return;
    try { await deleteRow('subramos', s.id); await loadSubramos(subRamoActivo.id); }
    catch { alert('No se puede eliminar — error inesperado.'); }
  };

  const deleteRamo = async (r) => {
    const count = ramoPolizas[r.id] || 0;
    if (count > 0) {
      setReplaceModal({ type:'ramo', item:r, count, replacementId:'', options: ramos.filter(x => x.id !== r.id) });
      return;
    }
    if (!window.confirm(`¿Eliminar ramo "${r.nombre}"? Se eliminarán también sus subramos y coberturas.`)) return;
    try { await deleteRow('ramos', r.id); await load(); }
    catch { alert('No se puede eliminar — error inesperado.'); }
  };

  const deleteAseguradora = async (a) => {
    const count = asegPolizas[a.id] || 0;
    if (count > 0) {
      setReplaceModal({ type:'aseguradora', item:a, count, replacementId:'', options: aseguradoras.filter(x => x.id !== a.id) });
      return;
    }
    if (!window.confirm(`¿Eliminar aseguradora "${a.nombre}"?`)) return;
    try {
      await supabase.from('aseguradora_ramos').delete().eq('aseguradora_id', a.id);
      await supabase.from('subramo_aseguradoras').delete().eq('aseguradora_id', a.id);
      await deleteRow('aseguradoras', a.id);
      await load();
    } catch (e) {
      alert('No se puede eliminar: ' + (e.message || 'error inesperado') + '. Puede tener solicitudes u otros registros asociados.');
    }
  };

  const confirmReplace = async () => {
    const { type, item, replacementId } = replaceModal;
    if (!replacementId) { alert('Selecciona un reemplazo.'); return; }
    const newId = parseInt(replacementId);
    try {
      if (type === 'ramo') {
        // Mover pólizas al nuevo ramo, limpiar subramo (podría no existir en el nuevo ramo)
        await supabase.from('polizas').update({ ramo_id: newId, subramo_id: null }).eq('ramo_id', item.id);
        await deleteRow('ramos', item.id);
        setReplaceModal(null);
        await load();
      } else if (type === 'aseguradora') {
        // Mover pólizas y solicitudes a la aseguradora de reemplazo, limpiar vínculos y eliminar
        await supabase.from('polizas').update({ aseguradora_id: newId }).eq('aseguradora_id', item.id);
        await supabase.from('solicitudes').update({ aseguradora_id: newId }).eq('aseguradora_id', item.id);
        await supabase.from('aseguradora_ramos').delete().eq('aseguradora_id', item.id);
        await supabase.from('subramo_aseguradoras').delete().eq('aseguradora_id', item.id);
        await deleteRow('aseguradoras', item.id);
        setReplaceModal(null);
        await load();
      } else {
        // Mover pólizas al nuevo subramo
        await supabase.from('polizas').update({ subramo_id: newId }).eq('subramo_id', item.id);
        await deleteRow('subramos', item.id);
        setReplaceModal(null);
        await loadSubramos(subRamoActivo.id);
      }
    } catch (e) { alert('Error al reemplazar: ' + (e.message || '')); }
  };

  const toggleSubramo = async (s) => {
    await updateRow('subramos', s.id, { activo: !s.activo });
    await loadSubramos(subRamoActivo.id);
  };

  const openAsegRamos = async (ramo) => {
    setRamoActivo(ramo);
    setAsegRamoForm({ aseguradora_id:'', comision:'' });
    setLoadingAsegRamos(true);
    const { data } = await supabase.from('aseguradora_ramos')
      .select('*, aseguradoras(nombre)').eq('ramo_id', ramo.id);
    setAsegRamos(data || []);
    setLoadingAsegRamos(false);
    setModal('aseg_ramos');
  };

  const saveAsegRamo = async () => {
    if (!asegRamoForm.aseguradora_id) return;
    try {
      await insertRow('aseguradora_ramos', {
        ramo_id: ramoActivo.id,
        aseguradora_id: parseInt(asegRamoForm.aseguradora_id),
        comision: asegRamoForm.comision ? parseFloat(asegRamoForm.comision) : null,
      });
      setAsegRamoForm({ aseguradora_id:'', comision:'' });
      const { data } = await supabase.from('aseguradora_ramos')
        .select('*, aseguradoras(nombre)').eq('ramo_id', ramoActivo.id);
      setAsegRamos(data || []);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const deleteAsegRamo = async (id) => {
    if (!window.confirm('¿Eliminar vinculación?')) return;
    await deleteRow('aseguradora_ramos', id);
    const { data } = await supabase.from('aseguradora_ramos')
      .select('*, aseguradoras(nombre)').eq('ramo_id', ramoActivo.id);
    setAsegRamos(data || []);
  };

  const saveCobertura = async () => {
    if (!cobForm.nombre.trim()) return;
    try {
      const payload = {
        ramo_id: ramoActivo.id,
        nombre: cobForm.nombre,
        descripcion: cobForm.descripcion || null,
        orden: parseInt(cobForm.orden) || 0,
        es_basica: cobForm.es_basica,
        activa: true,
      };
      if (cobSelected) {
        await updateRow('coberturas', cobSelected.id, payload);
      } else {
        await insertRow('coberturas', payload);
      }
      await loadCoberturas(ramoActivo.id);
      setCobForm({ nombre:'', descripcion:'', orden:0 });
      setCobSelected(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const editCobertura = (c) => {
    setCobSelected(c);
    setCobForm({ nombre:c.nombre, descripcion:c.descripcion||'', orden:c.orden||0 });
  };

  const deleteCobertura = async (c) => {
    if (!window.confirm(`¿Eliminar cobertura "${c.nombre}"?`)) return;
    try {
      await deleteRow('coberturas', c.id);
      await loadCoberturas(ramoActivo.id);
    } catch (e) { alert('No se puede eliminar.'); }
  };

  const toggleActivaCobertura = async (c) => {
    await updateRow('coberturas', c.id, { activa: !c.activa });
    await loadCoberturas(ramoActivo.id);
  };

  // ── Roles ─────────────────────────────────────────────────────────
  const openRol = (r = null) => {
    if (r) {
      setSelected(r); setForm({ nombre:r.nombre, descripcion:r.descripcion||'', ver_beneficiarios: !!r.ver_beneficiarios, ampliar_cupo_ia: !!r.ampliar_cupo_ia });
      try { setPermisos(typeof r.permisos === 'string' ? JSON.parse(r.permisos) : (r.permisos || defaultPermisos())); }
      catch { setPermisos(defaultPermisos()); }
    } else {
      setSelected(null); setForm({ nombre:'', descripcion:'', ver_beneficiarios:false, ampliar_cupo_ia:false }); setPermisos(defaultPermisos());
    }
    setModal('rol');
  };

  const saveRol = async () => {
    if (!form.nombre) return;
    try {
      const data = { nombre:form.nombre, descripcion:form.descripcion, permisos, es_master:false, ver_beneficiarios: !!form.ver_beneficiarios, ampliar_cupo_ia: !!form.ampliar_cupo_ia };
      if (selected) await updateRow('roles', selected.id, data);
      else await insertRow('roles', data);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const togglePermiso = (modulo, tipo) => {
    setPermisos(p => ({ ...p, [modulo]: { ...p[modulo], [tipo]: !p[modulo]?.[tipo] } }));
  };
  // Marca/desmarca una columna completa (ver o editar). Si editar=true, también marca ver.
  const setColumna = (tipo, valor) => {
    setPermisos(p => {
      const np = {};
      MODULOS.forEach(m => {
        const cur = p[m.key] || { ver:false, editar:false };
        np[m.key] = { ...cur, [tipo]: valor };
        if (tipo === 'editar' && valor) np[m.key].ver = true; // no se puede editar sin ver
      });
      return np;
    });
  };
  const setTodo = (valor) => {
    const np = {};
    MODULOS.forEach(m => { np[m.key] = { ver:valor, editar:valor }; });
    setPermisos(np);
  };

  const eliminarRol = async (r) => {
    if (!window.confirm('¿Eliminar rol?')) return;
    try { await deleteRow('roles', r.id); await load(); }
    catch { alert('No se puede eliminar — hay usuarios con este rol.'); }
  };

  // ── Usuarios ──────────────────────────────────────────────────────
  const openUsuario = (u = null, readOnly = false) => {
    if (u) { setSelected(u); setForm({
      primer_nombre:u.primer_nombre||'', segundo_nombre:u.segundo_nombre||'',
      primer_apellido:u.primer_apellido||'', segundo_apellido:u.segundo_apellido||'',
      cedula:u.cedula||'', fecha_nacimiento:u.fecha_nacimiento||'', dip_vencimiento:u.dip_vencimiento||'', cedula_url:u.cedula_url||'',
      nombre:u.nombre, username:u.username||'', email:u.email, rol_id:u.rol_id, ejecutivo_id:u.ejecutivo_id||'', agente_id:u.agente_id||'',
      ejec_cuenta:!!u.ejec_cuenta, ejec_cobro:!!u.ejec_cobro, ejec_reclamo:!!u.ejec_reclamo,
      password:'' }); }   // las contraseñas viven cifradas en Supabase Auth: nadie las ve
    else { setSelected(null); setForm({
      primer_nombre:'', segundo_nombre:'', primer_apellido:'', segundo_apellido:'',
      cedula:'', fecha_nacimiento:'', dip_vencimiento:'', cedula_url:'',
      nombre:'', username:'', email:'', rol_id:'', ejecutivo_id:'', agente_id:'',
      ejec_cuenta:false, ejec_cobro:false, ejec_reclamo:false, password:'' }); }
    setShowPwd(false); setCedulaFile(null);
    setUsuarioReadOnly(readOnly);
    setModal('usuario');
  };

  // Crear usuarios y asignar contraseñas requiere la llave `service_role`, que solo
  // existe en el servidor. Ver functions/api/admin-usuarios.js
  const llamarAdminUsuarios = async (cuerpo) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');
    const r = await fetch('/api/admin-usuarios', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(cuerpo),
    });
    const res = await r.json();
    if (!r.ok) throw new Error(res.error || 'No se pudo completar la operación');
    return res;
  };

  // Acceso de soporte consentido: abre (por la duración elegida) o cierra la
  // ventana llamando al servidor (functions/api/soporte.js), que desbanea/banea
  // al usuario de soporte y actualiza cont_config. Ver esa función para el modelo.
  const llamarSoporte = async (accion, extra = {}) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Tu sesión expiró. Vuelve a iniciar sesión.');
    const r = await fetch('/api/soporte', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ accion, ...extra }),
    });
    const res = await r.json();
    if (!r.ok) throw new Error(res.error || 'No se pudo completar la operación');
    return res;
  };

  const otorgarSoporte = async () => {
    const etiqueta = (DURACIONES_SOPORTE.find(d => d.min === soporteMin) || {}).label || `${soporteMin} min`;
    if (!window.confirm(`Vas a permitir que el equipo de Corredata entre a tu sistema durante ${etiqueta} para darte soporte. Todo lo que hagan queda registrado. ¿Continuar?`)) return;
    setSavingSoporte(true);
    try {
      await llamarSoporte('otorgar', { minutos: soporteMin });
      const c = await getConfig(); setCrmCfg(c || {});
      alert(`✓ Acceso de soporte habilitado por ${etiqueta}.`);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingSoporte(false); }
  };

  const revocarSoporte = async () => {
    setSavingSoporte(true);
    try {
      await llamarSoporte('revocar');
      const c = await getConfig(); setCrmCfg(c || {});
      alert('Acceso de soporte revocado.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingSoporte(false); }
  };

  const saveUsuario = async () => {
    if (savingUsuario) return;
    const partes = [form.primer_nombre, form.segundo_nombre, form.primer_apellido, form.segundo_apellido]
      .map(s => (s || '').trim()).filter(Boolean);
    const nombreCompleto = partes.join(' ') || (form.nombre || '').trim();
    if (!form.primer_nombre?.trim() && !nombreCompleto) return alert('El primer nombre es requerido.');
    if (!form.email?.trim()) return alert('El email es requerido.');
    if (form.agente_id && form.ejecutivo_id) return alert('Un usuario no puede ser agente y ejecutivo a la vez. Deja vinculado solo uno de los dos.');
    if (form.password) { const problema = problemaClave(form.password); if (problema) return alert(problema); }
    setSavingUsuario(true);
    try {
      const data = {
        nombre: nombreCompleto,
        primer_nombre: form.primer_nombre?.trim() || null,
        segundo_nombre: form.segundo_nombre?.trim() || null,
        primer_apellido: form.primer_apellido?.trim() || null,
        segundo_apellido: form.segundo_apellido?.trim() || null,
        cedula: form.cedula?.trim() || null,
        fecha_nacimiento: form.fecha_nacimiento || null,
        dip_vencimiento: form.dip_vencimiento || null,
        email: form.email.trim(),
        username: form.username?.trim() || null,
        rol_id: form.rol_id ? parseInt(form.rol_id) : null,
        ejecutivo_id: form.ejecutivo_id ? parseInt(form.ejecutivo_id) : null,
        agente_id: form.agente_id ? parseInt(form.agente_id) : null,
      };
      let usuarioId;
      if (selected) {
        await updateRow('usuarios', selected.id, data);
        usuarioId = selected.id;
        // La contraseña no se guarda aquí: la cambia Supabase Auth desde el servidor.
        if (form.password) await llamarAdminUsuarios({ accion:'cambiar_password', usuario_id: selected.id, password: form.password });
      } else {
        if (!form.password) { alert('Ponle una contraseña inicial al usuario nuevo.'); setSavingUsuario(false); return; }
        const { email, ...perfil } = data;
        const r = await llamarAdminUsuarios({ accion:'crear', email, password: form.password, perfil });
        usuarioId = r?.usuario?.id;
      }

      // Subir copia de cédula (si se eligió un archivo)
      let cedulaUrlFinal = form.cedula_url || null;
      if (cedulaFile && usuarioId) {
        try {
          const ext = cedulaFile.name.split('.').pop();
          const path = `usuarios/${usuarioId}/cedula_${Date.now()}.${ext}`;
          const { error: upErr } = await supabase.storage.from('solicitud-archivos').upload(path, cedulaFile, { upsert:true });
          if (upErr) throw upErr;
          const { data:{ publicUrl } } = supabase.storage.from('solicitud-archivos').getPublicUrl(path);
          await updateRow('usuarios', usuarioId, { cedula_url: publicUrl });
          cedulaUrlFinal = publicUrl;
        } catch (e) { alert('Usuario guardado, pero falló la subida de la cédula: ' + (e.message || e)); }
      }

      // Si el usuario es agente, el USUARIO es la fuente de la cédula: se copian
      // número, copia y vencimiento al agente vinculado para no cargarlos dos veces.
      if (data.agente_id) {
        try {
          await updateRow('agentes', data.agente_id, {
            cedula: data.cedula,
            dip_vencimiento: data.dip_vencimiento,
            cedula_url: cedulaUrlFinal,
          });
        } catch (e) { console.error('sync cédula → agente', e); }
      }

      try { await sincronizarAsignaciones(); } catch {}
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
    finally { setSavingUsuario(false); }
  };

  const toggleUsuario = async (u) => {
    const bloquear = u.activo; // si está activo, el clic lo bloquea
    if (bloquear && !window.confirm(`¿Bloquear a ${u.nombre}? No podrá iniciar sesión y se cerrará cualquier sesión que tenga abierta.`)) return;
    try {
      // Pasa por el servidor: desactiva la ficha Y banea en Auth (corta sesiones vivas).
      await llamarAdminUsuarios({ accion: 'bloquear', usuario_id: u.id, bloquear });
      await load();
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // Resetea la contraseña de un usuario a una temporal (la cambiará al entrar).
  const resetearPassword = async (u) => {
    const nueva = window.prompt(`Nueva contraseña TEMPORAL para ${u.nombre}.\nEl usuario deberá cambiarla en su próximo ingreso.`, '');
    if (nueva == null) return;
    const problema = problemaClave(nueva);
    if (problema) return alert(problema);
    try {
      await llamarAdminUsuarios({ accion: 'cambiar_password', usuario_id: u.id, password: nueva, debe_cambiar: true });
      alert('✓ Contraseña temporal asignada. El usuario la cambiará al entrar.');
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // ── Aseguradoras ──────────────────────────────────────────────────
  const saveAseguradora = async () => {
    if (!form.nombre) return;
    try {
      const data = { nombre:form.nombre, codigo:form.codigo||null, contacto:form.contacto||null, telefono:form.telefono||null, email:form.email||null, activa:form.activa !== false };
      if (selected) await updateRow('aseguradoras', selected.id, data);
      else await insertRow('aseguradoras', data);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  // ── Ramos ─────────────────────────────────────────────────────────
  const saveRamo = async () => {
    if (!form.nombre) return;
    try {
      const data = {
        nombre: form.nombre,
        codigo: form.codigo || null,
        activo: form.activo !== false,
        comision: form.comision ? parseFloat(form.comision) : null,
      };
      if (selected) await updateRow('ramos', selected.id, data);
      else await insertRow('ramos', data);
      await load(); setModal(null);
    } catch (e) { alert('Error: ' + (e.message || '')); }
  };

  const f  = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const cf = (k, v) => setCobForm(p => ({ ...p, [k]: v }));
  const sf = (k, v) => setSubForm(p => ({ ...p, [k]: v }));

  const TABS = [
    { key:'roles',        label:'Roles y Permisos', icon:Shield },
    { key:'usuarios',     label:'Usuarios',         icon:Users },
    // La marca solo la configura el master (el cliente y quien él designe).
    ...(user?.es_master ? [{ key:'marca', label:'Marca del sistema', icon:Palette }] : []),
    // Lo que se paga es decisión del dueño: solo el master ve y cambia el plan.
    ...(user?.es_master ? [{ key:'mi_plan', label:'Mi Plan', icon:DollarSign }] : []),
    { key:'aseguradoras', label:'Aseguradoras',      icon:Building },
    { key:'ramos',        label:'Ramos',             icon:Tag },
    { key:'ejecutivos',   label:'Ejecutivos',        icon:Users },
    { key:'grupos',       label:'Grupos Económicos', icon:Layers },
    { key:'documentos',   label:'Tipos de Documento', icon:FileText },
    { key:'catalogo',     label:'Catálogo Endoso/Póliza', icon:FileText },
    { key:'tipos_endoso', label:'Tipos de Endoso', icon:FileText },
    { key:'referidos',    label:'Referidos', icon:Users },
    { key:'notificaciones', label:'Notificaciones', icon:Bell },
  ];

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Administración</div><div className="page-subtitle">Configuración del sistema</div></div>
      </div>

      <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:24, background:'rgba(var(--navy-rgb),0.4)', borderRadius:10, padding:4 }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={()=>setTab(t.key)} style={{
              display:'flex', alignItems:'center', gap:7, padding:'7px 12px',
              borderRadius:8, border:'none', cursor:'pointer', fontFamily:'var(--font)',
              fontSize:13, fontWeight:600, whiteSpace:'nowrap',
              background: tab === t.key ? 'var(--sky)' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--silver)',
            }}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Tab: Roles ── */}
      {tab === 'roles' && (
        <div>
          <div className="toolbar"><button className="btn btn-primary btn-sm" onClick={()=>openRol()}><Plus size={14} /> Nuevo Rol</button></div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Descripción</th><th>Master</th><th>Acciones</th></tr></thead>
              <tbody>
                {roles.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight:600 }}>{r.nombre}</td>
                    <td className="text-silver">{r.descripcion || '—'}</td>
                    <td>{r.es_master ? <span className="badge badge-warning">Master</span> : '—'}</td>
                    <td>{!r.es_master && (
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>openRol(r)}><Edit2 size={13} /></button>
                        <button className="btn btn-icon btn-danger btn-sm" onClick={()=>eliminarRol(r)}><Trash2 size={13} /></button>
                      </div>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Marca del sistema (white-label) — solo master ── */}
      {tab === 'mi_plan' && user?.es_master && (
        <MiPlan cfg={crmCfg} usuarios={usuarios} modulosCatalogo={MODULOS}
          onCambio={async () => { const c = await getConfig(); setCrmCfg(c || {}); await refrescarMarca?.(); }} />
      )}

      {tab === 'marca' && user?.es_master && marcaForm && (
        <div>
          <div style={{ background:'rgba(var(--sky-rgb),0.06)', border:'1px solid rgba(var(--sky-rgb),0.2)', borderRadius:10, padding:'14px 18px', marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:4 }}>MARCA DEL SISTEMA</div>
            <div style={{ fontSize:12, color:'var(--silver)' }}>
              Personaliza el <strong>nombre</strong>, el <strong>logo</strong> y la <strong>paleta de colores</strong> del sistema.
              Los cambios se ven en vivo en la vista previa y aplican a toda la app al guardar. Los colores de estado
              (verde/ámbar/rojo) no se cambian: son señales universales.
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 340px', gap:20, alignItems:'start' }}>
            {/* ── Formulario ── */}
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Nombre del sistema</label>
                  <input className="form-input" value={marcaForm.marca_nombre || ''} placeholder="Nombre de tu corretaje"
                    onChange={e=>mf('marca_nombre', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Palabra resaltada (opcional)</label>
                  <input className="form-input" value={marcaForm.marca_nombre_corto || ''} placeholder="UNIDO"
                    onChange={e=>mf('marca_nombre_corto', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Tagline / lema</label>
                <input className="form-input" value={marcaForm.marca_tagline || ''} placeholder="Tu confianza, es nuestra cobertura."
                  onChange={e=>mf('marca_tagline', e.target.value)} />
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Abreviatura del corretaje</label>
                  <input className="form-input" value={marcaForm.marca_abreviatura || ''} placeholder="Corretaje"
                    onChange={e=>mf('marca_abreviatura', e.target.value)} />
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>
                    Cómo se nombra tu casa en las etiquetas: "Comisión {marcaForm.marca_abreviatura?.trim() || 'Corretaje'}", "Bolsa {marcaForm.marca_abreviatura?.trim() || 'Corretaje'}"… y el prefijo de los recibos.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Tipografía</label>
                  <select className="form-select" value={marcaForm.marca_fuente || 'Outfit'}
                    onChange={e=>mf('marca_fuente', e.target.value)}>
                    {FUENTES.map(f => (
                      <option key={f} value={f} style={{ fontFamily:`'${f}', sans-serif` }}>{f}</option>
                    ))}
                  </select>
                  <div style={{ fontSize:16, marginTop:6, fontFamily:`'${marcaForm.marca_fuente || 'Outfit'}', sans-serif` }}>
                    Aa Bb Cc — 123
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Logo</label>
                <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  {marcaForm.marca_logo_url ? (
                    <div style={{ height:44, padding:'4px 8px', background:'rgba(var(--navy-rgb),0.5)', border:'1px solid var(--border)', borderRadius:8, display:'flex', alignItems:'center' }}>
                      <img src={marcaForm.marca_logo_url} alt="Logo" style={{ height:34, width:'auto', maxWidth:160, objectFit:'contain' }} />
                    </div>
                  ) : (
                    <span style={{ fontSize:12, color:'var(--silver)' }}>Sin logo — se usa el de barras por defecto.</span>
                  )}
                  <label className="btn btn-secondary btn-sm" style={{ cursor:'pointer' }}>
                    {marcaForm.marca_logo_url ? 'Cambiar logo' : 'Subir logo'}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" style={{ display:'none' }}
                      onChange={e=>{ const f=e.target.files?.[0]; if (f) subirLogo(f); e.target.value=''; }} />
                  </label>
                  {marcaForm.marca_logo_url && (
                    <button className="btn btn-danger btn-sm" onClick={()=>{ mf('marca_logo_url',''); setLogoErr(''); }}>Quitar</button>
                  )}
                </div>
                <div style={{ fontSize:11, color:'var(--silver)', marginTop:6 }}>
                  PNG, JPG, WEBP o SVG. Se optimiza automáticamente (máx. 400px). Recomendado: PNG con fondo transparente o SVG.
                </div>
                {logoErr && <div style={{ fontSize:12, color:'var(--danger)', marginTop:4 }}>{logoErr}</div>}
                {marcaForm.marca_logo_url && (
                  <div style={{ marginTop:12 }}>
                    <label className="form-label">Tamaño del logo — {(Number(marcaForm.marca_logo_escala) || 1).toFixed(2)}×</label>
                    <input type="range" min="0.5" max="2" step="0.05" value={Number(marcaForm.marca_logo_escala) || 1}
                      onChange={e=>mf('marca_logo_escala', parseFloat(e.target.value))}
                      style={{ width:'100%', accentColor:'var(--sky)' }} />
                    <div style={{ fontSize:11, color:'var(--silver)' }}>Agranda el logo dentro de su espacio (no lo desborda).</div>
                  </div>
                )}
              </div>

              <div style={{ height:1, background:'var(--border)', margin:'4px 0' }} />
              <div style={{ fontSize:13, fontWeight:700, color:'var(--silver)' }}>Paleta de colores</div>
              <div className="form-grid form-grid-2">
                {[
                  { k:'color_navy',      label:'Fondo (Navy)' },
                  { k:'color_steel',     label:'Superficies (Steel)' },
                  { k:'color_sky',       label:'Primario (Sky)' },
                  { k:'color_sky_light', label:'Primario claro (Sky light)' },
                  { k:'color_silver',    label:'Texto tenue (Silver)' },
                ].map(c => (
                  <div className="form-group" key={c.k}>
                    <label className="form-label">{c.label}</label>
                    <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                      <input type="color" value={marcaForm[c.k] || '#000000'} onChange={e=>mf(c.k, e.target.value)}
                        style={{ width:44, height:38, padding:0, border:'1px solid var(--border)', borderRadius:8, background:'transparent', cursor:'pointer' }} />
                      <input className="form-input" value={marcaForm[c.k] || ''} onChange={e=>mf(c.k, e.target.value)}
                        style={{ fontFamily:'monospace', textTransform:'uppercase' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Legibilidad: se revisa mientras se eligen los colores (revisarLegibilidad, marca.js). */}
              {(() => {
                const problemas = problemasLegibilidad();
                if (problemas === null) return null;   // un color a medio escribir: no se juzga todavía
                return problemas.length === 0 ? (
                  <div style={{ fontSize:12.5, color:'var(--text-success)', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:8, padding:'8px 12px' }}>
                    ✓ Con estos colores todos los textos se leen bien.
                  </div>
                ) : (
                  <div style={{ fontSize:12.5, color:'var(--text)', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.4)', borderRadius:8, padding:'10px 12px', display:'grid', gap:6 }}>
                    <div style={{ fontWeight:700, color:'var(--text-warning)' }}>Con esta combinación algunos textos podrían no verse bien:</div>
                    {problemas.map((p, i) => (
                      <div key={i}>• {p.que} <span style={{ color:'var(--silver)' }}>— prueba ajustando <b>{p.cambia}</b></span></div>
                    ))}
                    <div style={{ color:'var(--silver)', fontSize:11.5 }}>Puedes guardarla igual, pero tu equipo podría tener problemas para leer esas partes del sistema.</div>
                  </div>
                );
              })()}

              <div style={{ display:'flex', gap:12, marginTop:4 }}>
                <button className="btn btn-primary" onClick={guardarMarca} disabled={savingMarca}>
                  {savingMarca ? 'Guardando…' : 'Guardar y aplicar'}
                </button>
                <button className="btn btn-secondary" onClick={restaurarMarcaDefault} disabled={savingMarca}>
                  Colores por defecto
                </button>
              </div>
            </div>

            {/* ── Vista previa en vivo ── */}
            <div style={{ position:'sticky', top:12 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', marginBottom:8, letterSpacing:0.5 }}>VISTA PREVIA</div>
              <div style={{ border:'1px solid var(--border)', borderRadius:12, overflow:'hidden', background:'linear-gradient(135deg, var(--navy), var(--navy-grad))' }}>
                <div style={{ padding:'16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:10 }}>
                  <LogoMarca size={28} marca={{ logo_url: marcaForm.marca_logo_url, logo_escala: marcaForm.marca_logo_escala }} />
                  <NombreMarca fontSize={16} marca={{ nombre: marcaForm.marca_nombre, nombre_corto: marcaForm.marca_nombre_corto }} />
                </div>
                <div style={{ padding:16, display:'flex', flexDirection:'column', gap:12 }}>
                  <div style={{ fontStyle:'italic', fontSize:12, color:'var(--silver)' }}>{marcaForm.marca_tagline || 'Tu confianza, es nuestra cobertura.'}</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <span className="btn btn-primary btn-sm">Primario</span>
                    <span className="btn btn-secondary btn-sm">Secundario</span>
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <span className="badge badge-info">Info</span>
                    <span className="badge badge-success">OK</span>
                    <span className="badge badge-warning">Alerta</span>
                    <span className="badge badge-danger">Error</span>
                  </div>
                  <div className="stat-card" style={{ padding:'14px 16px' }}>
                    <div className="stat-value" style={{ fontSize:22 }}>1,248</div>
                    <div className="stat-label">Pólizas activas</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize:11, color:'var(--silver)', marginTop:8 }}>
                Los colores se aplican en vivo a toda la pantalla; si sales sin guardar, se revierten.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Usuarios ── */}
      {tab === 'usuarios' && (
        <div>
          {/* Moneda del sistema */}
          <div style={{ background:'rgba(var(--sky-rgb),0.06)', border:'1px solid rgba(var(--sky-rgb),0.2)', borderRadius:10, padding:'14px 18px', marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:4 }}>MONEDA DEL SISTEMA</div>
            <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
              Todo el dinero del sistema se muestra con esta moneda (ej: <strong>B/. 1,000.00</strong>).
            </div>
            <div className="form-grid form-grid-3">
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Moneda</label>
                <select className="form-select" value={crmCfg.moneda || 'B/.'}
                  onChange={e=>setCrmCfg(c=>({ ...c, moneda: e.target.value }))}>
                  {MONEDAS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin:0, display:'flex', alignItems:'flex-end' }}>
                <button className="btn btn-primary btn-sm" onClick={guardarMoneda} disabled={savingCfg}>
                  {savingCfg ? 'Guardando...' : 'Guardar moneda'}
                </button>
              </div>
            </div>
          </div>
          {/* Responsables de gestión (a quién se auto-asignan las tareas de cobro y reclamos) */}
          <div style={{ background:'rgba(var(--sky-rgb),0.06)', border:'1px solid rgba(var(--sky-rgb),0.2)', borderRadius:10, padding:'14px 18px', marginBottom:18 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, marginBottom:4 }}>RESPONSABLES POR DEFECTO (RESPALDO)</div>
            <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
              Las tareas se asignan al ejecutivo de cobro / reclamo definido <strong>en cada póliza</strong>. Estos responsables por defecto se usan solo como respaldo cuando una póliza no tiene uno asignado.
            </div>
            <div className="form-grid form-grid-3">
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Ejecutivo de cobros</label>
                <select className="form-select" value={crmCfg.responsable_cobros_usuario_id||''}
                  onChange={e=>setCrmCfg(c=>({ ...c, responsable_cobros_usuario_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">Sin designar</option>
                  {usuarios.filter(u=>u.activo).map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Ejecutivo de reclamos</label>
                <select className="form-select" value={crmCfg.responsable_reclamos_usuario_id||''}
                  onChange={e=>setCrmCfg(c=>({ ...c, responsable_reclamos_usuario_id: e.target.value ? parseInt(e.target.value) : null }))}>
                  <option value="">Sin designar</option>
                  {usuarios.filter(u=>u.activo).map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin:0, display:'flex', alignItems:'flex-end' }}>
                <button className="btn btn-primary btn-sm" onClick={guardarResponsables} disabled={savingCfg}>
                  {savingCfg ? 'Guardando...' : 'Guardar responsables'}
                </button>
              </div>
            </div>
          </div>
          {/* Lo que incluye el plan y cómo cambiarlo vive en su propia pestaña: "Mi Plan". */}

          {/* Acceso de soporte consentido (solo master) */}
          {user?.es_master && (() => {
            const hasta = crmCfg.soporte_hasta ? new Date(crmCfg.soporte_hasta) : null;
            const activo = hasta && hasta.getTime() > Date.now();
            return (
              <div style={{ background: activo ? 'rgba(34,197,94,0.08)' : 'rgba(var(--sky-rgb),0.06)', border: `1px solid ${activo ? 'rgba(34,197,94,0.35)' : 'rgba(var(--sky-rgb),0.2)'}`, borderRadius:10, padding:'14px 18px', marginBottom:18 }}>
                <div style={{ fontSize:12, fontWeight:700, color: activo ? 'var(--text-success)' : 'var(--sky-light)', letterSpacing:1, marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
                  <Shield size={14} /> ACCESO DE SOPORTE
                </div>
                <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>
                  Permite que el equipo de <strong>Corredata</strong> entre a tu sistema por el <strong>tiempo que elijas</strong> para darte soporte. Sin tu permiso nadie externo entra, y todo lo que hagan queda registrado.
                </div>
                {activo ? (
                  <div style={{ display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                    <span style={{ fontSize:13, color:'var(--text)' }}>
                      ✓ Habilitado hasta <b>{hasta.toLocaleString()}</b>
                      {crmCfg.soporte_otorgado_por ? <> · autorizado por {crmCfg.soporte_otorgado_por}</> : null}
                    </span>
                    <button className="btn btn-sm btn-secondary" style={{ color:'var(--text-danger)', borderColor:'rgba(239,68,68,0.4)' }} onClick={revocarSoporte} disabled={savingSoporte}>
                      {savingSoporte ? '...' : 'Revocar acceso'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                    <label className="text-silver" style={{ fontSize:13 }}>Duración:</label>
                    <select className="form-select" style={{ width:'auto', minWidth:130 }}
                      value={soporteMin} onChange={e => setSoporteMin(Number(e.target.value))} disabled={savingSoporte}>
                      {DURACIONES_SOPORTE.map(d => <option key={d.min} value={d.min}>{d.label}</option>)}
                    </select>
                    <button className="btn btn-primary btn-sm" onClick={otorgarSoporte} disabled={savingSoporte}>
                      <Shield size={14} /> {savingSoporte ? 'Habilitando...' : 'Permitir soporte'}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
          {(() => {
            const limite = Number(crmCfg.limite_usuarios) || 0;
            const activos = usuarios.filter(u => u.activo && !u.es_soporte).length;
            const alcanzado = limite > 0 && activos >= limite;
            return (
              <div className="toolbar" style={{ alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <button className="btn btn-primary btn-sm" onClick={()=>openUsuario()}
                  disabled={alcanzado}
                  title={alcanzado ? `Tu plan admite ${limite} usuarios activos. Bloquea uno que ya no use el sistema, o amplía tu plan en la pestaña "Mi Plan".` : ''}
                  style={alcanzado ? { opacity:.5, cursor:'not-allowed' } : {}}>
                  <Plus size={14} /> Nuevo Usuario
                </button>
                {limite > 0 && (
                  <span className="text-silver" style={{ fontSize:13 }}>
                    {crmCfg.plan ? <b style={{ color:'var(--text)' }}>Plan {crmCfg.plan}</b> : null}
                    {crmCfg.plan ? ' · ' : ''}
                    <span style={{ color: alcanzado ? 'var(--text-danger)' : 'var(--text)' }}>{activos}</span> de {limite} usuarios
                    {alcanzado && ' — llegaste al tope de tu plan. '}
                    {alcanzado && (user?.es_master
                      ? <button onClick={() => setTab('mi_plan')} style={{ background:'none', border:'none', padding:0, cursor:'pointer', color:'var(--sky)', fontWeight:700, fontFamily:'inherit', fontSize:'inherit', textDecoration:'underline' }}>Agrega un usuario extra o sube de plan en Mi Plan</button>
                      : 'Bloquea un usuario que ya no use el sistema, o pídele al master que amplíe el plan en "Mi Plan".')}
                  </span>
                )}
              </div>
            );
          })()}
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Usuario</th><th>Email</th><th>Rol</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {usuarios.filter(u => !u.es_soporte).map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight:600 }}>{u.nombre}</td>
                    <td className="text-sky" style={{ fontFamily:'monospace', fontSize:12 }}>{u.username || '—'}</td>
                    <td className="text-silver">{u.email}</td>
                    <td><span className="badge badge-info">{u.rol_nombre || '—'}</span></td>
                    <td><span className={`badge ${u.activo ? 'badge-success' : 'badge-neutral'}`}>{u.activo ? 'Activo' : 'Inactivo'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-icon btn-secondary btn-sm" title="Ver" onClick={()=>openUsuario(u, true)}><Eye size={13} /></button>
                        <button className="btn btn-icon btn-secondary btn-sm" title="Editar" onClick={()=>openUsuario(u)}><Edit2 size={13} /></button>
                        <button className="btn btn-icon btn-secondary btn-sm" title="Resetear contraseña" onClick={()=>resetearPassword(u)}><KeyRound size={13} /></button>
                        <button className="btn btn-sm btn-secondary" style={u.activo ? { color:'var(--text-danger)', borderColor:'rgba(239, 68, 68, 0.4)' } : {}} onClick={()=>toggleUsuario(u)}>{u.activo ? 'Bloquear' : 'Reactivar'}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Aseguradoras ── */}
      {tab === 'aseguradoras' && (
        <div>
          <div className="toolbar">
            <button className="btn btn-primary btn-sm" onClick={()=>{ setSelected(null); setForm({ nombre:'', codigo:'', contacto:'', telefono:'', email:'', activa:true }); setModal('aseguradora'); }}>
              <Plus size={14} /> Nueva Aseguradora
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Código</th><th>Contacto</th><th>Teléfono</th><th style={{ textAlign:'center' }}>Pólizas</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {aseguradoras.map(a => (
                  <tr key={a.id}>
                    <td style={{ fontWeight:600 }}>{a.nombre}</td>
                    <td className="text-sky">{a.codigo || '—'}</td>
                    <td className="text-silver">{a.contacto || '—'}</td>
                    <td className="text-silver">{a.telefono || '—'}</td>
                    <td style={{ textAlign:'center' }}>
                      <span className={`badge ${(asegPolizas[a.id] || 0) > 0 ? 'badge-info' : 'badge-neutral'}`}>{asegPolizas[a.id] || 0}</span>
                    </td>
                    <td><span className={`badge ${a.activa ? 'badge-success' : 'badge-neutral'}`}>{a.activa ? 'Activa' : 'Inactiva'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-icon btn-secondary btn-sm" title="Editar" onClick={()=>{ setSelected(a); setForm({ ...a }); setModal('aseguradora'); }}><Edit2 size={13} /></button>
                        <button className="btn btn-icon btn-secondary btn-sm" title="Eliminar" style={{ color:'var(--text-danger)' }} onClick={()=>deleteAseguradora(a)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Ramos ── */}
      {tab === 'ramos' && (
        <div>
          <div className="toolbar">
            <button className="btn btn-primary btn-sm" onClick={()=>{ setSelected(null); setForm({ nombre:'', codigo:'', activo:true }); setModal('ramo'); }}>
              <Plus size={14} /> Nuevo Ramo
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Código</th><th>% Comisión</th><th>Estado</th><th>Pólizas</th><th>Subramos</th><th>Coberturas</th><th>Acciones</th></tr></thead>
              <tbody>
                {ramos.map(r => {
                  const pc = ramoPolizas[r.id] || 0;
                  return (
                  <tr key={r.id}>
                    <td style={{ fontWeight:600 }}>{r.nombre}</td>
                    <td className="text-sky">{r.codigo || '—'}</td>
                    <td className="text-silver">{r.comision != null ? `${r.comision}%` : '—'}</td>
                    <td><span className={`badge ${r.activo ? 'badge-success' : 'badge-neutral'}`}>{r.activo ? 'Activo' : 'Inactivo'}</span></td>
                    <td>
                      {pc > 0
                        ? <span className="badge badge-info">{pc} póliza{pc !== 1 ? 's' : ''}</span>
                        : <span className="text-silver" style={{ fontSize:12 }}>—</span>}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={()=>openSubramos(r)}>
                        <List size={12} /> Subramos
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-secondary" onClick={()=>openCoberturas(r)}>
                        <List size={12} /> Coberturas
                      </button>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ setSelected(r); setForm({ ...r, comision: r.comision != null ? String(r.comision) : '' }); setModal('ramo'); }}><Edit2 size={13} /></button>
                        <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteRamo(r)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ Modal: Rol ════════════════════════════════════════════ */}
      {modal === 'rol' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{selected ? 'Editar Rol' : 'Nuevo Rol'}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2" style={{ marginBottom:20 }}>
                <div className="form-group"><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre||''} onChange={e=>f('nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Descripción</label><input className="form-input" value={form.descripcion||''} onChange={e=>f('descripcion', e.target.value)} /></div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
                <div style={{ fontSize:14, fontWeight:700 }}>Permisos por módulo</div>
                <div style={{ display:'flex', gap:6 }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setTodo(true)}>✓ Marcar todo</button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setTodo(false)}>✕ Limpiar todo</button>
                </div>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Módulo</th>
                      <th style={{ textAlign:'center' }}>
                        Ver
                        <div style={{ display:'flex', gap:4, justifyContent:'center', marginTop:4 }}>
                          <button type="button" onClick={()=>setColumna('ver', true)} style={colBtn}>todas</button>
                          <button type="button" onClick={()=>setColumna('ver', false)} style={colBtn}>ninguna</button>
                        </div>
                      </th>
                      <th style={{ textAlign:'center' }}>
                        Editar
                        <div style={{ display:'flex', gap:4, justifyContent:'center', marginTop:4 }}>
                          <button type="button" onClick={()=>setColumna('editar', true)} style={colBtn}>todas</button>
                          <button type="button" onClick={()=>setColumna('editar', false)} style={colBtn}>ninguna</button>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODULOS.map(m => (
                      <tr key={m.key}>
                        <td style={{ fontWeight:600 }}>{m.label}</td>
                        <td style={{ textAlign:'center' }}><input type="checkbox" checked={!!permisos[m.key]?.ver} onChange={()=>togglePermiso(m.key, 'ver')} style={{ cursor:'pointer', accentColor:'var(--sky)', width:16, height:16 }} /></td>
                        <td style={{ textAlign:'center' }}><input type="checkbox" checked={!!permisos[m.key]?.editar} onChange={()=>togglePermiso(m.key, 'editar')} style={{ cursor:'pointer', accentColor:'var(--sky)', width:16, height:16 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop:16, padding:'12px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>DATOS CONFIDENCIALES</div>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13 }}>
                  <input type="checkbox" checked={!!form.ver_beneficiarios}
                    onChange={e=>setForm(f=>({ ...f, ver_beneficiarios: e.target.checked }))}
                    style={{ cursor:'pointer', accentColor:'var(--sky)', width:16, height:16 }} />
                  <span>Ver beneficiarios (información confidencial)</span>
                </label>
                <div style={{ fontSize:11, color:'var(--silver)', marginTop:6, marginLeft:26 }}>
                  Sin este permiso, el rol ve solo la fecha de última actualización de los beneficiarios, no los nombres. El master siempre los ve.
                </div>
              </div>
              <div style={{ marginTop:12, padding:'12px 14px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>GASTOS DEL SISTEMA</div>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', fontSize:13 }}>
                  <input type="checkbox" checked={!!form.ampliar_cupo_ia}
                    onChange={e=>setForm(f=>({ ...f, ampliar_cupo_ia: e.target.checked }))}
                    style={{ cursor:'pointer', accentColor:'var(--sky)', width:16, height:16 }} />
                  <span>Ampliar el cupo de análisis con IA</span>
                </label>
                <div style={{ fontSize:11, color:'var(--silver)', marginTop:6, marginLeft:26 }}>
                  Permite comprar paquetes de análisis cuando se agota el cupo del mes. <strong>Genera un cargo</strong> en la factura, por eso solo debe tenerlo quien pueda autorizar gastos. El master siempre puede.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveRol}>Guardar Rol</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Usuario ════════════════════════════════════════ */}
      {modal === 'usuario' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{usuarioReadOnly ? 'Ver Usuario' : (selected ? 'Editar Usuario' : 'Nuevo Usuario')}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight:'78vh', overflowY:'auto' }}>
              <fieldset disabled={usuarioReadOnly} style={{ border:'none', padding:0, margin:0, minWidth:0 }}>
              <div className="form-grid form-grid-2">
                <div className="form-group"><label className="form-label">Primer nombre *</label><input className="form-input" value={form.primer_nombre||''} onChange={e=>f('primer_nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Segundo nombre</label><input className="form-input" value={form.segundo_nombre||''} onChange={e=>f('segundo_nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Primer apellido</label><input className="form-input" value={form.primer_apellido||''} onChange={e=>f('primer_apellido', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Segundo apellido</label><input className="form-input" value={form.segundo_apellido||''} onChange={e=>f('segundo_apellido', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Cédula</label><input className="form-input" value={form.cedula||''} onChange={e=>f('cedula', e.target.value)} placeholder="8-888-8888" /></div>
                <div className="form-group"><label className="form-label">Vencimiento de cédula (DIP)</label><input className="form-input" type="date" value={form.dip_vencimiento||''} onChange={e=>f('dip_vencimiento', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Fecha de nacimiento</label><input className="form-input" type="date" value={form.fecha_nacimiento||''} onChange={e=>f('fecha_nacimiento', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Email *</label><input className="form-input" type="email" value={form.email||''} onChange={e=>f('email', e.target.value)} /></div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Nombre de usuario <span style={{color:'var(--silver)',fontWeight:400}}>(para iniciar sesión, ej. rgriffith)</span></label>
                  <input className="form-input" value={form.username||''} autoCapitalize="none"
                    onChange={e=>f('username', e.target.value.toLowerCase().replace(/\s+/g,''))} placeholder="rgriffith" />
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>Puede iniciar sesión con este usuario o con su correo. Debe ser único.</div>
                </div>
                <div className="form-group"><label className="form-label">Rol</label><select className="form-select" value={form.rol_id||''} onChange={e=>f('rol_id', e.target.value)}><option value="">Sin rol</option>{roles.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}</select></div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Ejecutivo vinculado <span style={{color:'var(--silver)',fontWeight:400}}>(para recibir tareas de sus pólizas)</span></label>
                  <select className="form-select" value={form.ejecutivo_id||''} disabled={!!form.agente_id && !form.ejecutivo_id}
                    onChange={e=>{ f('ejecutivo_id', e.target.value); if (e.target.value) f('agente_id', ''); }}
                    style={(!!form.agente_id && !form.ejecutivo_id) ? { opacity:0.5, cursor:'not-allowed' } : undefined}>
                    <option value="">Sin vincular</option>
                    {ejecutivos.filter(e=>e.activo).map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                  </select>
                  <div style={{ fontSize:11, color: form.agente_id ? '#f59e0b' : 'var(--silver)', marginTop:4 }}>
                    {form.agente_id
                      ? 'Este usuario está vinculado a un agente. Un agente no puede ser ejecutivo — quita el agente para poder vincularlo a una ficha de ejecutivo.'
                      : 'Si este usuario es ejecutivo, vincúlalo con su ficha para que el CRM le asigne las tareas de sus pólizas.'}
                  </div>
                </div>
                {form.ejecutivo_id && (() => {
                  const ej = ejecutivos.find(e => String(e.id) === String(form.ejecutivo_id));
                  const funcs = ej ? [ej.ejec_cuenta&&'Cuenta', ej.ejec_cobro&&'Cobro', ej.ejec_reclamo&&'Reclamo'].filter(Boolean) : [];
                  return (
                    <div className="form-group" style={{ gridColumn:'span 2' }}>
                      <label className="form-label">Funciones de ejecutivo <span style={{color:'var(--silver)',fontWeight:400}}>(heredadas de la ficha)</span></label>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:4 }}>
                        {funcs.length ? funcs.map(fn => <span key={fn} className="badge badge-info">{fn}</span>)
                          : <span className="text-silver text-sm">Esta ficha de ejecutivo no tiene funciones asignadas.</span>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>Se configuran en <strong>Admin → Ejecutivos</strong>, en la ficha del ejecutivo. El acceso a módulos (qué ve/edita) lo da el Rol de arriba.</div>
                    </div>
                  );
                })()}
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Agente vinculado <span style={{color:'var(--silver)',fontWeight:400}}>(si es un agente: solo verá SU cartera)</span></label>
                  <select className="form-select" value={form.agente_id||''} disabled={!!form.ejecutivo_id && !form.agente_id}
                    onChange={e=>{ f('agente_id', e.target.value); if (e.target.value) f('ejecutivo_id', ''); }}
                    style={(!!form.ejecutivo_id && !form.agente_id) ? { opacity:0.5, cursor:'not-allowed' } : undefined}>
                    <option value="">No es agente (ve según su rol)</option>
                    {agentesList.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                  </select>
                  <div style={{ fontSize:11, color:'var(--text-warning)', marginTop:4 }}>
                    {form.ejecutivo_id
                      ? 'Este usuario está vinculado a un ejecutivo. Un ejecutivo no puede ser agente — quita el ejecutivo para poder vincularlo a un agente.'
                      : 'Al vincularlo a un agente, el usuario solo verá las pólizas, cobros, comisiones y reclamos donde ese agente participa. (No aplica a usuarios Master.)'}
                  </div>
                </div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">
                    {selected ? 'Nueva contraseña' : 'Contraseña inicial *'}
                    <span style={{color:'var(--silver)',fontWeight:400}}>
                      {selected ? ' (déjala vacía para no cambiarla)' : ''}
                    </span>
                  </label>
                  <div style={{ position:'relative' }}>
                    <input className="form-input" type={showPwd ? 'text' : 'password'} value={form.password||''}
                      onChange={e=>f('password', e.target.value)} style={{ paddingRight:38, width:'100%' }}
                      autoComplete="new-password" />
                    <button type="button" onClick={()=>setShowPwd(s=>!s)}
                      title={showPwd ? 'Ocultar' : 'Ver lo que escribiste'}
                      style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--silver)', padding:4, display:'flex' }}>
                      {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>
                    {selected
                      ? 'Se guarda cifrada en Supabase Auth. Si la escribes, el usuario deberá cambiarla en su próximo ingreso. Déjala vacía para no tocarla.'
                      : 'Ponle una contraseña TEMPORAL (genérica está bien): el usuario la cambiará por una propia la primera vez que entre. Se guarda cifrada; nadie, ni el master, puede verla luego.'}
                  </div>
                </div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Copia de la cédula</label>
                  <input type="file" accept="image/*,application/pdf" onChange={e=>setCedulaFile(e.target.files?.[0] || null)}
                    style={{ fontSize:13, color:'var(--silver)' }} />
                  {cedulaFile && <div style={{ fontSize:12, color:'var(--sky-light)', marginTop:4 }}>Se subirá: {cedulaFile.name}</div>}
                  {!cedulaFile && form.cedula_url && (
                    <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(form.cedula_url); }} style={{ fontSize:12, color:'var(--sky-light)', display:'inline-flex', alignItems:'center', gap:5, marginTop:4 }}>
                      <FileText size={13}/> Ver cédula adjunta
                    </a>
                  )}
                </div>
              </div>
              </fieldset>
            </div>
            <div className="modal-footer">
              {usuarioReadOnly ? (
                <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={()=>setModal(null)} disabled={savingUsuario}>Cancelar</button>
                  <button className="btn btn-primary" onClick={saveUsuario} disabled={savingUsuario}>{savingUsuario ? 'Guardando...' : 'Guardar'}</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Aseguradora ═══════════════════════════════════ */}
      {modal === 'aseguradora' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{selected ? 'Editar Aseguradora' : 'Nueva Aseguradora'}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre||''} onChange={e=>f('nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Código</label><input className="form-input" value={form.codigo||''} onChange={e=>f('codigo', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Contacto</label><input className="form-input" value={form.contacto||''} onChange={e=>f('contacto', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono||''} onChange={e=>f('telefono', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" value={form.email||''} onChange={e=>f('email', e.target.value)} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveAseguradora}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Ramo ══════════════════════════════════════════ */}
      {modal === 'ramo' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{selected ? 'Editar Ramo' : 'Nuevo Ramo'}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre||''} onChange={e=>f('nombre', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Código</label><input className="form-input" value={form.codigo||''} onChange={e=>f('codigo', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">% Comisión Estándar</label><input className="form-input" type="number" step="0.01" value={form.comision||''} onChange={e=>f('comision', e.target.value)} placeholder="Ej: 10" /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveRamo}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Ejecutivos ── */}
      {tab === 'ejecutivos' && (
        <div>
          <div className="toolbar">
            <button className="btn btn-primary btn-sm" onClick={()=>{ setSelected(null); setForm({ nombre:'', email:'', telefono:'', activo:true, ejec_cuenta:false, ejec_cobro:false, ejec_reclamo:false }); setModal('ejecutivo'); }}>
              <Plus size={14}/> Nuevo Ejecutivo
            </button>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Email</th><th>Teléfono</th><th>Funciones</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {ejecutivos.map(e => {
                  const funcs = [e.ejec_cuenta&&'Cuenta', e.ejec_cobro&&'Cobro', e.ejec_reclamo&&'Reclamo'].filter(Boolean);
                  return (
                  <tr key={e.id}>
                    <td style={{ fontWeight:600 }}>{e.nombre}</td>
                    <td className="text-silver">{e.email||'—'}</td>
                    <td className="text-silver">{e.telefono||'—'}</td>
                    <td>{funcs.length ? <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>{funcs.map(fn => <span key={fn} className="badge badge-info">{fn}</span>)}</div> : <span className="text-silver">—</span>}</td>
                    <td><span className={`badge ${e.activo?'badge-success':'badge-neutral'}`}>{e.activo?'Activo':'Inactivo'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ setSelected(e); setForm({...e}); setModal('ejecutivo'); }}><Edit2 size={13}/></button>
                        <button className="btn btn-sm btn-secondary" onClick={()=>toggleEjecutivo(e)}>{e.activo?'Desactivar':'Activar'}</button>
                      </div>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ Tab: Grupos Económicos ═══════════════════════════════ */}
      {tab === 'grupos' && (
        <div>
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>{grupoSelected ? 'Editar Grupo' : 'Nuevo Grupo Económico'}</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr auto', gap:10, alignItems:'flex-end' }}>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={grupoForm.nombre} onChange={e=>setGrupoForm(g=>({...g,nombre:e.target.value}))} placeholder="Ej: Grupo Familiar Pérez"/>
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Descripción</label>
                <input className="form-input" value={grupoForm.descripcion} onChange={e=>setGrupoForm(g=>({...g,descripcion:e.target.value}))} placeholder="Opcional"/>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button className="btn btn-primary btn-sm" onClick={saveGrupo}>
                  <Plus size={14}/> {grupoSelected ? 'Actualizar' : 'Crear'}
                </button>
                {grupoSelected && (
                  <button className="btn btn-secondary btn-sm" onClick={()=>{ setGrupoSelected(null); setGrupoForm({ nombre:'', descripcion:'' }); }}>
                    Cancelar
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Descripción</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {grupos.length === 0
                  ? <tr><td colSpan={4} style={{ textAlign:'center', color:'var(--silver)', padding:20 }}>Sin grupos económicos creados</td></tr>
                  : grupos.map(g => (
                    <tr key={g.id}>
                      <td style={{ fontWeight:600 }}>{g.nombre}</td>
                      <td className="text-silver">{g.descripcion || '—'}</td>
                      <td><span className={`badge ${g.activo ? 'badge-success' : 'badge-neutral'}`}>{g.activo ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ setGrupoSelected(g); setGrupoForm({ nombre:g.nombre, descripcion:g.descripcion||'' }); }}>
                            <Edit2 size={13}/>
                          </button>
                          <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteGrupo(g.id)}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Tipos de Documento ── */}
      {tab === 'documentos' && (
        <div>
          {/* Formulario inline */}
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>
              {tipoDocSelected ? 'Editar Tipo de Documento' : 'Nuevo Tipo de Documento'}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto', gap:10, alignItems:'flex-end' }}>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={tipoDocForm.nombre}
                  onChange={e => setTipoDocForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder="Ej: Estados Financieros" />
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Código</label>
                <input className="form-input" value={tipoDocForm.codigo}
                  onChange={e => setTipoDocForm(f => ({ ...f, codigo: e.target.value }))}
                  placeholder="Ej: EEFF" />
              </div>
              <div className="form-group" style={{ margin:0 }}>
                <label className="form-label">Categoría</label>
                <select className="form-select" value={tipoDocForm.categoria}
                  onChange={e => setTipoDocForm(f => ({ ...f, categoria: e.target.value }))}>
                  <option value="cliente_natural">Cliente Natural</option>
                  <option value="cliente_juridico">Cliente Jurídico</option>
                  <option value="poliza">Póliza</option>
                  <option value="solicitud">Solicitud</option>
                  <option value="auto">Auto</option>
                  <option value="reclamo">Reclamo</option>
                  <option value="cobro">Cobros</option>
                  <option value="otro">Otros</option>
                </select>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--silver)', cursor:'pointer', whiteSpace:'nowrap' }}>
                  <input type="checkbox" checked={tipoDocForm.requiere_anio}
                    onChange={e => setTipoDocForm(f => ({ ...f, requiere_anio: e.target.checked }))}
                    style={{ accentColor:'var(--sky)', width:14, height:14 }} />
                  Requiere año
                </label>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveTipoDoc} disabled={savingTipoDoc}>
                    <Plus size={14} /> {tipoDocSelected ? 'Actualizar' : 'Crear'}
                  </button>
                  {tipoDocSelected && (
                    <button className="btn btn-secondary btn-sm"
                      onClick={() => { setTipoDocSelected(null); setTipoDocForm({ nombre:'', codigo:'', categoria:'otro', requiere_anio:false }); }}>
                      Cancelar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Código</th>
                  <th>Categoría</th>
                  <th>Requiere Año</th>
                  <th>Orden</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tiposDoc.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--silver)', padding:20 }}>Sin tipos de documento. Ejecuta el SQL de migración primero.</td></tr>
                  : tiposDoc.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontWeight:600 }}>{t.nombre}</td>
                      <td className="text-sky">{t.codigo || '—'}</td>
                      <td className="text-silver" style={{ fontSize:12 }}>
                        {{ cliente_natural:'Cliente Natural', cliente_juridico:'Cliente Jurídico', poliza:'Póliza', solicitud:'Solicitud', auto:'Auto', reclamo:'Reclamo', cobro:'Cobros', otro:'Otros' }[t.categoria] || t.categoria}
                      </td>
                      <td>{t.requiere_anio ? <span className="badge badge-info">Sí</span> : <span className="text-silver" style={{fontSize:12}}>No</span>}</td>
                      <td className="text-silver">{t.orden}</td>
                      <td>
                        <span className={`badge ${t.activo ? 'badge-success' : 'badge-neutral'}`}>
                          {t.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-icon btn-secondary btn-sm"
                            onClick={() => {
                              setTipoDocSelected(t);
                              setTipoDocForm({ nombre:t.nombre, codigo:t.codigo||'', categoria:t.categoria, requiere_anio:!!t.requiere_anio });
                            }}>
                            <Edit2 size={13} />
                          </button>
                          <button
                            className={`btn btn-sm ${t.activo ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ fontSize:11, padding:'2px 8px' }}
                            onClick={() => toggleTipoDoc(t)}>
                            {t.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Catálogo Endoso/Póliza ── */}
      {tab === 'catalogo' && (
        <div>
          <div style={{ marginBottom:12, fontSize:13, color:'var(--silver)' }}>
            Tipos de documento que aparecen al adjuntar archivos en solicitudes de endoso y póliza.
          </div>
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>
              {catEndosoSelected ? 'Editar Tipo' : 'Nuevo Tipo'}
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
              <div className="form-group" style={{ margin:0, flex:1 }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={catEndosoForm.nombre}
                  onChange={e => setCatEndosoForm({ nombre: e.target.value })}
                  placeholder="Ej: Contrato" />
              </div>
              <button className="btn btn-primary btn-sm" onClick={saveCatEndoso} disabled={savingCatEndoso}>
                <Plus size={14} /> {catEndosoSelected ? 'Actualizar' : 'Crear'}
              </button>
              {catEndosoSelected && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setCatEndosoSelected(null); setCatEndosoForm({ nombre:'' }); }}>
                  Cancelar
                </button>
              )}
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Orden</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {catEndoso.length === 0
                  ? <tr><td colSpan={4} style={{ textAlign:'center', color:'var(--silver)', padding:20 }}>Sin tipos en el catálogo.</td></tr>
                  : catEndoso.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight:600 }}>{c.nombre}</td>
                      <td className="text-silver">{c.orden}</td>
                      <td><span className={`badge ${c.activo ? 'badge-success' : 'badge-neutral'}`}>{c.activo ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-icon btn-secondary btn-sm"
                            onClick={() => { setCatEndosoSelected(c); setCatEndosoForm({ nombre:c.nombre }); }}>
                            <Edit2 size={13} />
                          </button>
                          <button className={`btn btn-sm ${c.activo ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ fontSize:11, padding:'2px 8px' }}
                            onClick={() => toggleCatEndoso(c)}>
                            {c.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'tipos_endoso' && (
        <div>
          <div style={{ marginBottom:12, fontSize:13, color:'var(--silver)' }}>
            Tipos de endoso que aparecen al crear una solicitud de endoso (para llevar el tracking de qué
            le pediste a la aseguradora). El <strong>ramo</strong> decide en qué pólizas aparece cada tipo
            (<em>Todos los ramos</em> = siempre; o solo el ramo elegido). <strong>Qué cambia (target)</strong>
            define qué campo de la póliza toca el sistema al facturar (suma, cobertura, datos, vigencia…);
            <em> Sin cambio estructural</em> = solo descriptivo. El <strong>comportamiento</strong> define qué hace
            al facturar: <em>Normal</em> = movimiento de prima; <em>Cancelación</em> = cancela la póliza + clawback;
            <em> Prórroga</em> = extiende la vigencia (fianzas).
          </div>
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>
              {tipoEndosoSelected ? 'Editar Tipo de Endoso' : 'Nuevo Tipo de Endoso'}
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div className="form-group" style={{ margin:0, flex:'2 1 220px' }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={tipoEndosoForm.nombre}
                  onChange={e => setTipoEndosoForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Aumento de suma asegurada" />
              </div>
              <div className="form-group" style={{ margin:0, flex:'1 1 160px' }}>
                <label className="form-label">Ramo</label>
                <select className="form-select" value={tipoEndosoForm.ramo_id}
                  onChange={e => setTipoEndosoForm(p => ({ ...p, ramo_id: e.target.value }))}>
                  <option value="">Todos los ramos</option>
                  {ramos.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin:0, flex:'1 1 220px' }}>
                <label className="form-label">Qué cambia (target)</label>
                <select className="form-select" value={tipoEndosoForm.target}
                  onChange={e => setTipoEndosoForm(p => ({ ...p, target: e.target.value }))}>
                  {ENDOSO_TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ margin:0, flex:'1 1 180px' }}>
                <label className="form-label">Comportamiento</label>
                <select className="form-select" value={tipoEndosoForm.comportamiento}
                  onChange={e => setTipoEndosoForm(p => ({ ...p, comportamiento: e.target.value }))}>
                  <option value="normal">Normal (movimiento de prima)</option>
                  <option value="cancelacion">Cancelación de póliza</option>
                  <option value="prorroga">Prórroga de vigencia (fianza)</option>
                </select>
              </div>
              <button className="btn btn-primary btn-sm" onClick={saveTipoEndoso} disabled={savingTipoEndoso}>
                <Plus size={14} /> {tipoEndosoSelected ? 'Actualizar' : 'Crear'}
              </button>
              {tipoEndosoSelected && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setTipoEndosoSelected(null); setTipoEndosoForm({ nombre:'', comportamiento:'normal', target:'libre', ramo_id:'' }); }}>
                  Cancelar
                </button>
              )}
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Ramo</th><th>Qué cambia</th><th>Comportamiento</th><th>Orden</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {tipoEndoso.length === 0
                  ? <tr><td colSpan={7} style={{ textAlign:'center', color:'var(--silver)', padding:20 }}>Sin tipos de endoso en el catálogo.</td></tr>
                  : tipoEndoso.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight:600 }}>{c.nombre}</td>
                      <td className="text-silver">{c.ramo_id ? (ramos.find(r => r.id === c.ramo_id)?.nombre || '—') : 'Todos'}</td>
                      <td className="text-silver">{targetLabel(c.target)}</td>
                      <td className="text-silver">
                        {c.comportamiento === 'cancelacion' ? 'Cancelación'
                          : c.comportamiento === 'prorroga' ? 'Prórroga'
                          : 'Normal'}
                      </td>
                      <td className="text-silver">{c.orden}</td>
                      <td><span className={`badge ${c.activo ? 'badge-success' : 'badge-neutral'}`}>{c.activo ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-icon btn-secondary btn-sm"
                            onClick={() => { setTipoEndosoSelected(c); setTipoEndosoForm({ nombre:c.nombre, comportamiento:c.comportamiento||'normal', target:c.target||'libre', ramo_id:c.ramo_id?String(c.ramo_id):'' }); }}>
                            <Edit2 size={13} />
                          </button>
                          <button className={`btn btn-sm ${c.activo ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ fontSize:11, padding:'2px 8px' }}
                            onClick={() => toggleTipoEndoso(c)}>
                            {c.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Referidos ── */}
      {tab === 'referidos' && (
        <div>
          <div style={{ marginBottom:12, fontSize:13, color:'var(--silver)' }}>
            Un <strong>referido</strong> (referidor) pasa pólizas pero no trabaja activo como agente.
            Tiene su catálogo propio y gana una comisión (% sobre la prima) por la póliza que refiere.
          </div>
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ fontSize:13, fontWeight:700, marginBottom:12 }}>
              {referidoSelected ? 'Editar Referido' : 'Nuevo Referido'}
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div className="form-group" style={{ margin:0, flex:'2 1 200px' }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={referidoForm.nombre}
                  onChange={e => setReferidoForm(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Juan Pérez" />
              </div>
              <div className="form-group" style={{ margin:0, flex:'1 1 140px' }}>
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={referidoForm.telefono}
                  onChange={e => setReferidoForm(p => ({ ...p, telefono: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin:0, flex:'1 1 180px' }}>
                <label className="form-label">Email</label>
                <input className="form-input" value={referidoForm.email}
                  onChange={e => setReferidoForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="form-group" style={{ margin:0, flex:'0 1 120px' }}>
                <label className="form-label">% Nueva</label>
                <input className="form-input" type="number" step="0.01" value={referidoForm.comision_nueva_pct}
                  onChange={e => setReferidoForm(p => ({ ...p, comision_nueva_pct: e.target.value }))}
                  placeholder="Ej: 5" />
              </div>
              <div className="form-group" style={{ margin:0, flex:'0 1 120px' }}>
                <label className="form-label">% Renovación</label>
                <input className="form-input" type="number" step="0.01" value={referidoForm.comision_renovacion_pct}
                  onChange={e => setReferidoForm(p => ({ ...p, comision_renovacion_pct: e.target.value }))}
                  placeholder="Ej: 3" />
              </div>
              <div className="form-group" style={{ margin:0, flex:'2 1 220px' }}>
                <label className="form-label">Notas</label>
                <input className="form-input" value={referidoForm.notas}
                  onChange={e => setReferidoForm(p => ({ ...p, notas: e.target.value }))} />
              </div>
              <button className="btn btn-primary btn-sm" onClick={saveReferido} disabled={savingReferido}>
                <Plus size={14} /> {referidoSelected ? 'Actualizar' : 'Crear'}
              </button>
              {referidoSelected && (
                <button className="btn btn-secondary btn-sm"
                  onClick={() => { setReferidoSelected(null); setReferidoForm({ nombre:'', telefono:'', email:'', comision_nueva_pct:'', comision_renovacion_pct:'', notas:'' }); }}>
                  Cancelar
                </button>
              )}
            </div>
          </div>
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Nombre</th><th>Teléfono</th><th>Email</th><th>% Nueva</th><th>% Renovación</th><th>Notas</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {referidos.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--silver)', padding:20 }}>Sin referidos creados.</td></tr>
                  : referidos.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight:600 }}>{r.nombre}</td>
                      <td className="text-silver">{r.telefono || '—'}</td>
                      <td className="text-silver">{r.email || '—'}</td>
                      <td className="text-silver">{(r.comision_nueva_pct ?? r.comision_pct) != null ? `${r.comision_nueva_pct ?? r.comision_pct}%` : '—'}</td>
                      <td className="text-silver">{r.comision_renovacion_pct != null ? `${r.comision_renovacion_pct}%` : '—'}</td>
                      <td className="text-silver">{r.notas || '—'}</td>
                      <td><span className={`badge ${r.activo ? 'badge-success' : 'badge-neutral'}`}>{r.activo ? 'Activo' : 'Inactivo'}</span></td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn-icon btn-secondary btn-sm"
                            onClick={() => { setReferidoSelected(r); setReferidoForm({ nombre:r.nombre||'', telefono:r.telefono||'', email:r.email||'', comision_nueva_pct:(r.comision_nueva_pct ?? r.comision_pct) != null ? String(r.comision_nueva_pct ?? r.comision_pct) : '', comision_renovacion_pct:r.comision_renovacion_pct != null ? String(r.comision_renovacion_pct) : '', notas:r.notas||'' }); }}>
                            <Edit2 size={13} />
                          </button>
                          <button className={`btn btn-sm ${r.activo ? 'btn-secondary' : 'btn-primary'}`}
                            style={{ fontSize:11, padding:'2px 8px' }}
                            onClick={() => toggleReferido(r)}>
                            {r.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: Notificaciones ── */}
      {tab === 'notificaciones' && (
        <div>
          {/* Interruptor maestro global (solo master) */}
          <div style={{ marginBottom:16, padding:16, borderRadius:10,
            background: notifConfig.habilitado ? 'rgba(34, 197, 94, 0.08)' : 'rgba(var(--silver-rgb),0.06)',
            border: `1px solid ${notifConfig.habilitado ? 'rgba(34,197,94,0.3)' : 'rgba(143,168,192,0.2)'}` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700, display:'flex', alignItems:'center', gap:8 }}>
                  <Bell size={16}/> Notificaciones por correo —{' '}
                  {notifConfig.habilitado
                    ? <span style={{ color:'var(--text-success)' }}>ACTIVADAS</span>
                    : <span style={{ color:'var(--silver)' }}>DESACTIVADAS</span>}
                </div>
                <div style={{ fontSize:12, color:'var(--silver)', marginTop:4, maxWidth:600 }}>
                  {notifConfig.habilitado
                    ? 'El envío está activo. En el CRM se pueden enviar los correos de las plantillas que estén activas.'
                    : 'Mientras esté desactivado, en el CRM los botones de enviar correo salen deshabilitados para todos. Las plantillas se pueden preparar desde ya; actívalo solo cuando la API key del proveedor esté puesta en Cloudflare.'}
                </div>
              </div>
              {user?.es_master ? (
                <button className={`btn ${notifConfig.habilitado ? 'btn-secondary' : 'btn-primary'}`} onClick={toggleNotifHabilitado}>
                  {notifConfig.habilitado ? 'Desactivar envío' : 'Activar envío'}
                </button>
              ) : (
                <span className="badge badge-neutral">Solo el usuario master puede activarlo</span>
              )}
            </div>
          </div>

          {/* Remitente */}
          <div style={{ marginBottom:20, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, fontWeight:700, marginBottom:12 }}>
              <Mail size={15}/> Remitente
            </div>
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div className="form-group" style={{ margin:0, flex:'1 1 200px' }}>
                <label className="form-label">Nombre del remitente</label>
                <input className="form-input" value={notifConfig.remitente_nombre}
                  onChange={e=>setNotifConfig(p=>({...p, remitente_nombre:e.target.value}))} placeholder="Nombre de tu corretaje" />
              </div>
              <div className="form-group" style={{ margin:0, flex:'2 1 260px' }}>
                <label className="form-label">Correo del remitente</label>
                <input className="form-input" value={notifConfig.remitente_correo}
                  onChange={e=>setNotifConfig(p=>({...p, remitente_correo:e.target.value}))} placeholder="comunicaciones@riesgounido.com" />
              </div>
              <button className="btn btn-primary btn-sm" onClick={saveNotifConfig} disabled={savingNotif==='config'}>
                {savingNotif==='config' ? 'Guardando...' : 'Guardar remitente'}
              </button>
            </div>
            <div style={{ fontSize:11, color:'var(--silver)', marginTop:8 }}>
              La API key del proveedor NO se guarda aquí (va como variable segura en Cloudflare). Para enviar desde tu
              dominio hay que verificarlo con el proveedor (registros DNS).
            </div>
          </div>

          {/* Plantillas */}
          {NOTIF_TIPOS.map(t => {
            const pl = notifPlantillas[t.key] || { tipo:t.key, activo:false, asunto:'', cuerpo:'' };
            return (
              <div key={t.key} style={{ marginBottom:16, padding:16, background:'rgba(var(--steel-rgb),0.3)', borderRadius:10, border:'1px solid rgba(var(--silver-rgb),0.15)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
                  <div style={{ fontSize:14, fontWeight:700 }}>{t.label}</div>
                  <button className={`btn btn-sm ${pl.activo ? 'btn-primary' : 'btn-secondary'}`} style={{ fontSize:11, padding:'3px 10px' }}
                    onClick={()=>toggleNotifPlantilla(t.key)}>
                    {pl.activo ? '● Activa' : '○ Inactiva'}
                  </button>
                </div>
                <div style={{ fontSize:12, color:'var(--silver)', marginBottom:12 }}>{t.desc}</div>
                <div className="form-group" style={{ marginBottom:10 }}>
                  <label className="form-label">Asunto</label>
                  <input className="form-input" value={pl.asunto || ''} onChange={e=>setPlantillaField(t.key,'asunto',e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom:10 }}>
                  <label className="form-label">Cuerpo del mensaje</label>
                  <textarea className="form-textarea" rows={6} value={pl.cuerpo || ''}
                    onChange={e=>setPlantillaField(t.key,'cuerpo',e.target.value)} style={{ resize:'vertical' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
                  <div style={{ fontSize:11, color:'var(--silver)' }}>
                    Variables: {t.vars.map(v=>(
                      <code key={v} style={{ background:'rgba(var(--sky-rgb),0.15)', color:'var(--sky-light)', padding:'1px 6px', borderRadius:4, marginRight:6, fontSize:11 }}>{`{{${v}}}`}</code>
                    ))}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={()=>saveNotifPlantilla(t.key)} disabled={savingNotif===t.key}>
                    {savingNotif===t.key ? 'Guardando...' : 'Guardar plantilla'}
                  </button>
                </div>
                {t.key === 'cumpleanos' && (
                  <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid rgba(var(--silver-rgb),0.12)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                    <div style={{ fontSize:12, color:'var(--silver)', flex:'1 1 240px' }}>
                      Envío <strong>automático diario</strong>: cada mañana el sistema revisa quién cumple años y le manda este correo (cuando el envío esté activo).
                    </div>
                    {user?.es_master && (
                      <button className="btn btn-secondary btn-sm" onClick={enviarCumpleanosHoy} disabled={enviandoCumple}>
                        {enviandoCumple ? 'Enviando...' : 'Enviar cumpleaños de hoy (probar)'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ══ Modal: Ejecutivo ══════════════════════════════════════ */}
      {modal === 'ejecutivo' && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{selected ? 'Editar Ejecutivo' : 'Nuevo Ejecutivo'}</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div className="form-grid form-grid-2">
                <div className="form-group" style={{ gridColumn:'span 2' }}><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre||''} onChange={e=>f('nombre',e.target.value)}/></div>
                <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email||''} onChange={e=>f('email',e.target.value)}/></div>
                <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono||''} onChange={e=>f('telefono',e.target.value)}/></div>
                <div className="form-group" style={{ gridColumn:'span 2' }}>
                  <label className="form-label">Funciones <span style={{color:'var(--silver)',fontWeight:400}}>(puede tener varias)</span></label>
                  <div style={{ display:'flex', gap:18, flexWrap:'wrap', marginTop:4 }}>
                    {[['ejec_cuenta','Cuenta (renovación/endoso)'],['ejec_cobro','Cobro'],['ejec_reclamo','Reclamo']].map(([k,lbl])=>(
                      <label key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!form[k]} onChange={e=>f(k, e.target.checked)} style={{ accentColor:'var(--sky)', width:15, height:15, cursor:'pointer' }} />
                        {lbl}
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:'var(--silver)', marginTop:4 }}>Determina en qué selector de la póliza aparece este ejecutivo. El usuario que se vincule a esta ficha hereda estas funciones.</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={saveEjecutivo}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Vinculación Aseguradora-Ramo ══════════════════ */}
      {/* ══ Modal: Subramos del Ramo ════════════════════════════ */}
      {modal === 'subramos' && subRamoActivo && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Subramos — {subRamoActivo.nombre}</div>
                <div className="text-silver text-sm">Subdivide el ramo para mayor precisión en pólizas e importaciones</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div style={{ background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--silver)' }}>
                  {subSelected ? 'EDITAR SUBRAMO' : 'AGREGAR SUBRAMO'}
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={subForm.nombre} onChange={e=>sf('nombre', e.target.value)} placeholder="Ej: Daños a Terceros" />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Código</label>
                    <input className="form-input" value={subForm.codigo} onChange={e=>sf('codigo', e.target.value)} placeholder="Opcional" />
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
                  {subSelected && (
                    <button className="btn btn-secondary btn-sm" onClick={()=>{ setSubSelected(null); setSubForm({ nombre:'', codigo:'' }); }}>Cancelar</button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={saveSubramo}>
                    {subSelected ? 'Guardar Cambios' : <><Plus size={13}/> Agregar</>}
                  </button>
                </div>
              </div>

              {subramos.length === 0 ? (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:'24px 0' }}>Sin subramos definidos para este ramo.</div>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Nombre</th><th>Código</th><th>Pólizas</th><th>Coberturas</th><th>Aseguradoras</th><th>Estado</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {subramos.map(s => {
                        const spc = subPolizas[s.id] || 0;
                        return (
                        <tr key={s.id}>
                          <td style={{ fontWeight:600 }}>{s.nombre}</td>
                          <td className="text-silver">{s.codigo || '—'}</td>
                          <td>
                            {spc > 0
                              ? <span className="badge badge-info">{spc} póliza{spc !== 1 ? 's' : ''}</span>
                              : <span className="text-silver" style={{ fontSize:12 }}>—</span>}
                          </td>
                          <td>
                            <button className="btn btn-sm btn-secondary" onClick={()=>openCobSubramo(s)}>
                              <List size={12}/> Coberturas
                            </button>
                          </td>
                          <td>
                            <button className="btn btn-sm btn-secondary" onClick={()=>openSubAseg(s)}>
                              <Link size={12}/> {s.subramo_aseguradoras?.length || 0} vinculada{s.subramo_aseguradoras?.length !== 1 ? 's' : ''}
                            </button>
                          </td>
                          <td><span className={`badge ${s.activo ? 'badge-success' : 'badge-neutral'}`}>{s.activo ? 'Activo' : 'Inactivo'}</span></td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>{ setSubSelected(s); setSubForm({ nombre:s.nombre, codigo:s.codigo||'' }); }}><Edit2 size={12}/></button>
                              <button className="btn btn-sm btn-secondary" onClick={()=>toggleSubramo(s)} style={{ fontSize:11 }}>{s.activo ? 'Desactivar' : 'Activar'}</button>
                              <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteSubramo(s)}><Trash2 size={12}/></button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Aseguradoras del Subramo ════════════════════ */}
      {modal === 'subramo_aseg' && subAsegActivo && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Aseguradoras — {subAsegActivo.nombre}</div>
                <div className="text-silver text-sm">Cada aseguradora puede tener su propio % de comisión y base de cálculo</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--silver)' }}>VINCULAR ASEGURADORA</div>
                <div className="form-grid form-grid-3">
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Aseguradora *</label>
                    <select className="form-select" value={subAsegForm.aseguradora_id} onChange={e=>setSubAsegForm(f=>({...f,aseguradora_id:e.target.value}))}>
                      <option value="">— Seleccionar —</option>
                      {aseguradoras.filter(a=>a.activa!==false && !subAsegList.find(l=>l.aseguradora_id===a.id)).map(a=>(
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">% Comisión</label>
                    <input className="form-input" type="number" step="0.01" value={subAsegForm.porcentaje_comision}
                      onChange={e=>setSubAsegForm(f=>({...f,porcentaje_comision:e.target.value}))} placeholder="Ej: 12.5" />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Base de cálculo</label>
                    <select className="form-select" value={subAsegForm.base_comision} onChange={e=>setSubAsegForm(f=>({...f,base_comision:e.target.value}))}>
                      <option value="sin_impuesto">Sin impuesto</option>
                      <option value="con_impuesto">Con impuesto</option>
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveSubAseg}><Plus size={13}/> Vincular</button>
                </div>
              </div>

              {subAsegList.length === 0 ? (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:'24px 0' }}>Sin aseguradoras vinculadas a este subramo.</div>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Aseguradora</th><th>% Comisión</th><th>Base cálculo</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {subAsegList.map(l => (
                        <tr key={l.id}>
                          <td style={{ fontWeight:600 }}>{l.aseguradoras?.nombre}</td>
                          <td className="text-sky">{l.porcentaje_comision != null ? `${l.porcentaje_comision}%` : '—'}</td>
                          <td style={{ fontSize:11 }}>
                            {l.base_comision === 'con_impuesto'
                              ? <span style={{ color:'var(--silver)' }}>Con impuesto</span>
                              : <span style={{ color:'var(--text-success)' }}>Sin impuesto</span>}
                          </td>
                          <td>
                            <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteSubAseg(l.id)}><Trash2 size={12}/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Coberturas del Subramo ══════════════════════ */}
      {modal === 'cob_subramo' && cobSubramoActivo && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Coberturas — {cobSubramoActivo.nombre}</div>
                <div className="text-silver text-sm">Define las coberturas básicas y adicionales de este subramo</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--silver)' }}>AGREGAR COBERTURA</div>
                <div className="form-grid form-grid-3">
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={cobSubForm.nombre}
                      onChange={e=>setCobSubForm(f=>({...f,nombre:e.target.value}))} placeholder="Ej: Lesiones Corporales" />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Orden</label>
                    <input className="form-input" type="number" value={cobSubForm.orden}
                      onChange={e=>setCobSubForm(f=>({...f,orden:e.target.value}))} placeholder="0" />
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <label className="form-label">Descripción / Texto de la cobertura</label>
                    <textarea className="form-textarea" rows={3} value={cobSubForm.descripcion}
                      onChange={e=>setCobSubForm(f=>({...f,descripcion:e.target.value}))} placeholder="Texto de la cobertura (opcional)" />
                  </div>
                  <div className="form-group" style={{ margin:0, display:'flex', alignItems:'center', gap:10, paddingTop:22 }}>
                    <input type="checkbox" id="esBasicaNew" checked={cobSubForm.es_basica}
                      onChange={e=>setCobSubForm(f=>({...f,es_basica:e.target.checked}))}
                      style={{ accentColor:'var(--sky)', width:15, height:15, cursor:'pointer' }}/>
                    <label htmlFor="esBasicaNew" style={{ fontSize:13, cursor:'pointer' }}>Cobertura básica</label>
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveCobSubramo}><Plus size={13}/> Agregar</button>
                </div>
              </div>

              {/* Básicas */}
              {cobSubramo.filter(c=>c.es_basica).length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--text-success)', letterSpacing:1, marginBottom:8 }}>BÁSICAS (incluidas por defecto)</div>
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th>Ord.</th><th>Nombre</th><th>Descripción</th><th>Acciones</th></tr></thead>
                      <tbody>
                        {cobSubramo.filter(c=>c.es_basica).map(c=>(
                          <tr key={c.id}>
                            <td className="text-silver" style={{ textAlign:'center', width:50 }}>{c.orden}</td>
                            <td style={{ fontWeight:600 }}>{c.nombre}</td>
                            <td className="text-silver">{c.descripcion ? <CoberturaDesc descripcion={c.descripcion} /> : '—'}</td>
                            <td>
                              <div style={{ display:'flex', gap:6 }}>
                                <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={()=>toggleCobSubramoBasica(c)} title="Cambiar a adicional">→ Adicional</button>
                                <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteCobSubramo(c.id)}><Trash2 size={12}/></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Adicionales */}
              {cobSubramo.filter(c=>!c.es_basica).length > 0 && (
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:8 }}>ADICIONALES (opcionales)</div>
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th>Ord.</th><th>Nombre</th><th>Descripción</th><th>Acciones</th></tr></thead>
                      <tbody>
                        {cobSubramo.filter(c=>!c.es_basica).map(c=>(
                          <tr key={c.id}>
                            <td className="text-silver" style={{ textAlign:'center', width:50 }}>{c.orden}</td>
                            <td style={{ fontWeight:600 }}>{c.nombre}</td>
                            <td className="text-silver">{c.descripcion ? <CoberturaDesc descripcion={c.descripcion} /> : '—'}</td>
                            <td>
                              <div style={{ display:'flex', gap:6 }}>
                                <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={()=>toggleCobSubramoBasica(c)} title="Promover a básica">→ Básica</button>
                                <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteCobSubramo(c.id)}><Trash2 size={12}/></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {cobSubramo.length === 0 && (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:'24px 0' }}>Sin coberturas definidas. Agrega básicas primero.</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Coberturas del Ramo ═══════════════════════════ */}
      {modal === 'coberturas' && ramoActivo && (
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Coberturas — {ramoActivo.nombre}</div>
                <div className="text-silver text-sm">Plantilla de coberturas predefinidas para solicitudes</div>
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {/* Form agregar / editar */}
              <div style={{ background:'rgba(var(--steel-rgb),0.3)', borderRadius:8, padding:'14px 16px', marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, marginBottom:10, color:'var(--silver)' }}>
                  {cobSelected ? 'EDITAR COBERTURA' : 'AGREGAR COBERTURA'}
                </div>
                <div className="form-grid form-grid-3">
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={cobForm.nombre}
                      onChange={e=>cf('nombre', e.target.value)} placeholder="Ej: Responsabilidad Civil" />
                  </div>
                  <div className="form-group" style={{ margin:0 }}>
                    <label className="form-label">Orden</label>
                    <input className="form-input" type="number" value={cobForm.orden}
                      onChange={e=>cf('orden', e.target.value)} placeholder="0" />
                  </div>
                  <div className="form-group" style={{ gridColumn:'span 2', margin:0 }}>
                    <label className="form-label">Descripción / Texto de la cobertura</label>
                    <textarea className="form-textarea" rows={3} value={cobForm.descripcion}
                      onChange={e=>cf('descripcion', e.target.value)} placeholder="Texto de la cobertura (opcional)" />
                  </div>
                  <div className="form-group" style={{ margin:0, display:'flex', alignItems:'center', gap:10, paddingTop:22 }}>
                    <input type="checkbox" id="esBasicaRamo" checked={cobForm.es_basica}
                      onChange={e=>cf('es_basica', e.target.checked)}
                      style={{ accentColor:'var(--sky)', width:15, height:15, cursor:'pointer' }}/>
                    <label htmlFor="esBasicaRamo" style={{ fontSize:13, cursor:'pointer' }}>Básica</label>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:12, justifyContent:'flex-end' }}>
                  {cobSelected && (
                    <button className="btn btn-secondary btn-sm" onClick={()=>{ setCobSelected(null); setCobForm({ nombre:'', descripcion:'', orden:0, es_basica:true }); }}>
                      Cancelar
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" onClick={saveCobertura}>
                    {cobSelected ? 'Guardar Cambios' : <><Plus size={13}/> Agregar</>}
                  </button>
                </div>
              </div>

              {/* Lista */}
              {coberturas.length === 0 ? (
                <div className="text-silver text-sm" style={{ textAlign:'center', padding:'24px 0' }}>
                  Sin coberturas definidas para este ramo.
                </div>
              ) : (
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Orden</th><th>Nombre</th><th>Descripción</th><th>Estado</th><th>Acciones</th></tr></thead>
                    <tbody>
                      {coberturas.map(c => (
                        <tr key={c.id}>
                          <td className="text-silver" style={{ textAlign:'center' }}>{c.orden}</td>
                          <td style={{ fontWeight:600 }}>{c.nombre}</td>
                          <td className="text-silver">{c.descripcion ? <CoberturaDesc descripcion={c.descripcion} /> : '—'}</td>
                          <td>
                            <span className={`badge ${c.activa ? 'badge-success' : 'badge-neutral'}`}>
                              {c.activa ? 'Activa' : 'Inactiva'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display:'flex', gap:6 }}>
                              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>editCobertura(c)} title="Editar"><Edit2 size={12}/></button>
                              <button className="btn btn-sm btn-secondary" onClick={()=>toggleActivaCobertura(c)} style={{ fontSize:11 }}>
                                {c.activa ? 'Desactivar' : 'Activar'}
                              </button>
                              <button className="btn btn-icon btn-danger btn-sm" onClick={()=>deleteCobertura(c)} title="Eliminar"><Trash2 size={12}/></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setModal(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      {/* ══ Modal: Reemplazo antes de eliminar ═══════════════════ */}
      {replaceModal && (
        <div className="modal-overlay" onClick={()=>setReplaceModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                {replaceModal.type === 'ramo' ? 'Eliminar Ramo con Reemplazo'
                  : replaceModal.type === 'aseguradora' ? 'Eliminar Aseguradora con Reemplazo'
                  : 'Eliminar Subramo con Reemplazo'}
              </div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={()=>setReplaceModal(null)}><X size={16}/></button>
            </div>
            <div className="modal-body">
              <div style={{ background:'rgba(239, 68, 68, 0.1)', border:'1px solid rgba(239, 68, 68, 0.3)', borderRadius:8, padding:'12px 16px', marginBottom:20 }}>
                <div style={{ fontWeight:700, color:'var(--text-danger)', marginBottom:4 }}>
                  "{replaceModal.item.nombre}" tiene {replaceModal.count} póliza{replaceModal.count !== 1 ? 's' : ''} asociada{replaceModal.count !== 1 ? 's' : ''}
                </div>
                <div style={{ fontSize:13, color:'var(--silver)' }}>
                  Selecciona {replaceModal.type === 'ramo' ? 'el ramo' : replaceModal.type === 'aseguradora' ? 'la aseguradora' : 'el subramo'} de reemplazo. Todas las pólizas serán migradas automáticamente antes de eliminar.
                  {replaceModal.type === 'ramo' && <span> El subramo de cada póliza se limpiará — deberás reasignarlo si aplica.</span>}
                  {replaceModal.type === 'aseguradora' && <span> También se moverán las solicitudes y se limpiarán los vínculos de ramos/subramos.</span>}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  {replaceModal.type === 'ramo' ? 'Ramo de reemplazo *' : replaceModal.type === 'aseguradora' ? 'Aseguradora de reemplazo *' : 'Subramo de reemplazo *'}
                </label>
                <select
                  className="form-select"
                  value={replaceModal.replacementId}
                  onChange={e=>setReplaceModal(m=>({...m, replacementId:e.target.value}))}
                >
                  <option value="">— Seleccionar —</option>
                  {replaceModal.options.map(o=>(
                    <option key={o.id} value={o.id}>{o.nombre}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setReplaceModal(null)}>Cancelar</button>
              <button
                className="btn btn-danger"
                onClick={confirmReplace}
                disabled={!replaceModal.replacementId}
                style={{ opacity: replaceModal.replacementId ? 1 : 0.5 }}
              >
                Reemplazar y Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
